import * as colorUtils from '@sosocio/frontend-utils/color';
import * as svgUtils from '@sosocio/frontend-utils/svg';
import ProductState from 'classes/productstate';
import experiment from 'controllers/experiment';
import type {
	CropFromRatioOptions,
	EditorInteractivePageObjectResizeSide,
	FullOfferingModel,
	FullPageModel,
	FullPageModels,
	MovePageObjectCropOptions,
	MovePageObjectCropResult,
	MovePageObjectOptions,
	MovePageObjectResult,
	OfferingOptionsToolbarProductOptionValueModel,
	PageObjectModelChangeResult,
	ResizePageObjectCropOptions,
	ResizePageObjectCropResult,
	ResizePageObjectOptions,
	SnapGuideOrientation,
	SnapGuides,
	SnapGuideType,
	SnapMovedObjectOptions,
	SnapResizedObjectOptions,
	TemplateTextPosition,
	ZoomObjectResult,
} from 'interfaces/app';
import type {
	OfferingModel,
	OfferingOptionModel,
	OfferingOptionValueModel,
	PhotoModel,
	ProductModel,
	SpineModel,
	TemplatePositionTextAttributesModel,
	TemplatePositionTextModel,
} from 'interfaces/database';
import type {
	PageModel,
	PageObjectModel,
	PageStateModel,
	ProductSettings,
} from 'interfaces/project';
import { nanoid } from 'nanoid';
import * as DialogService from 'services/dialog';
import {
	COLOR_FULL,
	OfferingGroups,
	PRINT_COLOR_SPECTRUM_FULL,
} from 'settings/offerings';
import URL_PARAMETERS from 'settings/url-parameters';
import {
	COVER_BACK,
	COVER_BACK_INSIDE,
	COVER_FRONT,
	COVER_FRONT_INSIDE,
	SPINE,
} from 'settings/values';
import {
	AppDataModule,
	ConfigModule,
	FontModule,
	ProductStateModule,
} from 'store';
import { resolveOfferingOptionValue } from 'store/modules/appdata';
import defaults from 'store/modules/productstate/defaults';
import {
	createDomElement,
	domElementToHTMLElement,
	insertSoftHyphens,
	stripHtmlEntities,
	textareaToDomElementText,
} from 'utils/dom';
import {
	getEffectiveLineHeight,
	getFontFamily,
	MIN_FONTSIZE,
	pointToPixel,
} from 'utils/font';
import reportError from 'utils/report-error';
import {
	filter,
	fit,
} from 'utils/text';
import { watch } from 'vue';
import type { LocationQuery } from 'vue-router';
import appendUrlParameter from './append-url-parameter';
import * as mobileTools from './is-mobile';
import isPresignedUrl from './is-presigned-url';
import {
	getPageObjectCropBounds,
	normalizeCropRect,
	normalizePageObjectCrop,
} from './page-object-crop';
import {
	hasTemplatePositionTextAttributes,
	normalizeTemplatePositionTextAttributes,
} from './template-position-text-attributes';

type BackendOnlyFixedPageStateModel = Pick<PageStateModel, 'id' | 'pageModel' | 'serialnumber'>;

interface CalculateObjectResizeOptions {
	aspectRatio: number | null;
	fixedCornerIndex: FixedCornerIndex;
	initialPoints: Point[];
	maxSizeLimit?: number;
	minSizeLimit: number;
	resizeFactor: number;
	resizeSide: EditorInteractivePageObjectResizeSide;
}

/**
 * The index of the fixed corner (0-3) that remains unchanged during resizing
 * 		- 0: Top-left
 * 		- 1: Top-right
 * 		- 2: Bottom-right
 * 		- 3: Bottom-left
 */
type FixedCornerIndex = 0 | 1 | 2 | 3;

/**
 * Options that control which guides are considered active.
 */
interface GetActiveSnapGuidesOptions {
	/**
	 * Predicate that returns `true` when a guide should be skipped from the
	 * active-set computation (for example, guidelines that live inside a magnetic
	 * band that already snapped the object).
	 */
	excludeGuide?: (
		guide: MagneticGuide,
		orientation: SnapGuideOrientation,
	) => boolean;
}

interface GetOfferingOptionValueLabelOptions {
	fullOfferingModels: FullOfferingModel[];
	offeringModel: OfferingModel;
	offeringOption: OptionalExceptFor<OfferingOptionModel, 'tag'>;
	productOptionValue: OptionalExceptFor<OfferingOptionsToolbarProductOptionValueModel, 'id' | 'offeringid' | 'value'>;
	productSettings?: ProductSettings;
}

interface InsertBackendOnlyFixedPagesForViewingOptions {
	fixedPageStateModels: BackendOnlyFixedPageStateModel[];
	offeringModel: OfferingModel;
	pageModels: PageModel[];
}

/**
 * Describes the snapping helpers for a single axis.
 */
interface MagneticAxisLines {
	/**
	 * Ranged magnetic bands (bleed/page margins) expressed as inclusive
	 * `[min, max]` values in page coordinates.
	 */
	bands: MagneticBand[];
	/**
	 * Point-based guides (edges, centres, thirds, etc.).
	 */
	guides: MagneticGuide[];
}

/**
 * Inclusive range describing a magnetic band.
 */
interface MagneticBand {
	/**
	 * Leading bound (page coordinates).
	 */
	min: number;
	/**
	 * Trailing bound (page coordinates).
	 */
	max: number;
}

/**
 * Describes a single point-based snapping guide.
 */
interface MagneticGuide {
	/**
	 * Absolute position in page units.
	 */
	position: number;
	/**
	 * Object anchors that should coincide with the guide.
	 */
	target: MagneticGuideTarget | MagneticGuideTarget[];
	/**
	 * Optional tolerance override. Falls back to the axis default when omitted.
	 */
	tolerance?: number;
	/**
	 * Visual classification (edge, centre, …).
	 */
	type: SnapGuideType;
}

/**
 * Alignment anchor to apply against a guide:
 * - `'start'`: leading edge (minX/minY)
 * - `'end'`: trailing edge (maxX/maxY)
 * - `'center'`: midpoint ((min + max) / 2)
 * Provide an array to allow the same guide to align multiple anchors.
 */
type MagneticGuideTarget = 'center' | 'start' | 'end';

/**
 * A magnetic guide paired with its axis orientation.
 */
interface OrientedMagneticGuide extends MagneticGuide {
	/**
	 * Axis orientation (`vertical` for X, `horizontal` for Y).
	 */
	orientation: SnapGuideOrientation;
}

interface PageMagneticLines {
	x: MagneticAxisLines;
	y: MagneticAxisLines;
}

interface Point {
	x: number;
	y: number;
}

/**
 * 1 for increase, -1 for decrease, 0 for no change
 */
type ResizeDirection = -1 | 0 | 1;

interface ResizeParameter {
	/**
	 * The index of the fixed corner/side that remains unchanged during resizing
	 */
	fixedCornerIndex: FixedCornerIndex;
	/**
	 * Increase, decrease or no change on the height resize
	 */
	heightDirection: ResizeDirection;
	/**
	 * Increase, decrease or no change on the width resize
	 */
	widthDirection: ResizeDirection;
}

/**
 * Parameters to indicate the direction of the height and width adjustment
 * when resizing an object and also the fixed corner/size to remain unchanged
 */
type ResizeParameters = {
	[side in EditorInteractivePageObjectResizeSide]: ResizeParameter;
};

interface RotationSnapRange {
	/**
	 * Inclusive lower bound of the rotation interval (in degrees).
	 */
	start: number;
	/**
	 * Inclusive upper bound of the rotation interval (in degrees).
	 */
	end: number;
	/**
	 * Candidate snap targets (in degrees) associated with this interval.
	 * Each entry must live within `[start, end]`.
	 */
	targets: number[];
}

interface ScaleTarget {
	height: number;
	width: number;
}

/**
 * Potential snap adjustment for a given axis.
 */
interface ShiftCandidate {
	/**
	 * Magnetic band that produced this candidate (if any).
	 */
	bandRange?: MagneticBand;
	/**
	 * Guide that produced this candidate (if any).
	 */
	guide?: OrientedMagneticGuide;
	/**
	 * Resulting translation delta in page units.
	 */
	shift: number;
}

/**
 * Result of the move snapping pass.
 */
interface SnapMovedObjectResult {
	/**
	 * Axis tolerance used when resolving guide snaps.
	 */
	centerSnapTolerance: number;
	/**
	 * Snapped object geometry.
	 */
	objectModel: PageObjectModel;
	/**
	 * Magnetic configuration used for this resolution.
	 */
	pageMagneticLines: PageMagneticLines;
	/**
	 * Guides that are currently active for UI feedback.
	 */
	snapGuides: SnapGuides;
}

export interface AddTextObjectOptions {
	objectModel: Partial<PageObjectModel>;
	offeringModel?: OptionalExceptFor<OfferingModel, 'minfontsize'>;
	pageModel: PageModel;
	pageObjects: PageObjectModel[];
}

export interface BoundingBox {
	minX: number;
	maxX: number;
	minY: number;
	maxY: number;
}

export interface BoundingBoxRect {
	height: number;
	width: number;
	x: number;
	y: number;
}

export interface NormalizeViewingPageIdsOptions {
	backCoverPageId?: PageModel['id'] | null;
	frontCoverPageId?: PageModel['id'] | null;
}

export interface UpdateTextObjectOptions {
	newData: Partial<PageObjectModel>;
	objectModel: PageObjectModel;
	offeringModel?: OptionalExceptFor<OfferingModel, 'minfontsize'>;
	pageModel: PageModel;
}

const DEGREES_TO_RADIANS = Math.PI / 180;

/**
 * Finite-difference step used for derivative estimates.
 * Small compared with typical page units but large vs. floating-point noise.
 */
const epsilonPerturb = 1e-5;

/**
 * Threshold below which a derivative / determinant is considered zero.
 * Roughly (machine-epsilon ÷ `epsilonPerturb`) with a safety margin.
 */
const epsilonZero = 1e-10;

/**
 * Minimum snap tolerance (in screen pixels) for page guides.
 * Provides a generous capture area (can be tweaked if needed)
 * while avoiding accidental snaps.
 */
const GUIDE_SNAP_BASE_TOLERANCE = 10;

const MINIMUM_CROP_SIZE = 1;

const MINIMUM_VISIBLE_PIXELS = 50;

const RADIANS_TO_DEGREES = 180 / Math.PI;

/**
 * Resize parameters for each corner and side of the object
 */
const resizeParameters: ResizeParameters = {
	/**
	 * Corner `bottom-right` fixed to `top-left` corner
	 */
	'bottom-right': {
		widthDirection: 1,
		heightDirection: 1,
		fixedCornerIndex: 0,
	},
	/**
	 * Corner `top-left` fixed to `bottom-right` corner
	 */
	'top-left': {
		widthDirection: -1,
		heightDirection: -1,
		fixedCornerIndex: 2,
	},
	/**
	 * Corner `top-right` fixed to `bottom-left` corner
	 */
	'top-right': {
		widthDirection: 1,
		heightDirection: -1,
		fixedCornerIndex: 3,
	},
	/**
	 * Corner `bottom-left` fixed to `top-right` corner
	 */
	'bottom-left': {
		widthDirection: -1,
		heightDirection: 1,
		fixedCornerIndex: 1,
	},
	/**
	 * Side `bottom` fixed to `top` side, since the width it's not changing
	 * fixing to the `top-left` corner is valid
	 */
	bottom: {
		widthDirection: 0,
		heightDirection: 1,
		fixedCornerIndex: 0,
	},
	/**
	 * Side `top` fixed to `bottom` side, since the width it's not changing
	 * fixing to the `bottom-right` corner is valid
	 */
	top: {
		widthDirection: 0,
		heightDirection: -1,
		fixedCornerIndex: 2,
	},
	/**
	 * Side `right` fixed to `left` side, since the height it's not changing
	 * fixing to the `top-left` corner is valid
	 */
	right: {
		widthDirection: 1,
		heightDirection: 0,
		fixedCornerIndex: 0,
	},
	/**
	 * Side `left` fixed to the `right` side, since the height it's not changing
	 * fixing to the `top-right` corner is valid
	 */
	left: {
		widthDirection: -1,
		heightDirection: 0,
		fixedCornerIndex: 1,
	},
};

/**
 * Inclusive rotation intervals (in degrees) defined across 0°–180° that
 * snap to their nearest listed target. Negative counterparts are handled
 * by mirroring the magnitude at runtime.
 */
const ROTATION_SNAP_ABSOLUTE_RANGES: RotationSnapRange[] = [
	{
		start: 0,
		end: 5,
		targets: [
			0,
			5,
		],
	},
	{
		start: 40,
		end: 50,
		targets: [
			40,
			45,
			50,
		],
	},
	{
		start: 85,
		end: 95,
		targets: [
			85,
			90,
			95,
		],
	},
	{
		start: 130,
		end: 140,
		targets: [
			130,
			135,
			140,
		],
	},
	{
		start: 175,
		end: 180,
		targets: [
			175,
			180,
		],
	},
];

export const BACKEND_ONLY_FIXED_PAGE_ID_PREFIX = 'backendOnlyFixedPage:';

/**
 * Adjusts the object's position (`x_axis`, `y_axis`) after resizing to ensure
 * that the specified fixed corner remains anchored at its original location.
 *
 * This function is essential for maintaining the user's intent during resizing,
 * especially when snapping to magnetic bands. It calculates the necessary
 * position shift based on the difference between the original and new positions
 * of the fixed corner after resizing.
 *
 * @param newWidth The target width after resizing
 * @param newHeight The target height after resizing
 * @param baseObject The object before resizing, used as a base for adjustments
 * @param fixedCornerIndex The index of the corner to keep fixed:
 * 		0: top-left
 * 		1: top-right
 * 		2: bottom-right
 * 		3: bottom-left
 * @param initialPoints The rotated corner positions of the object before resizing
 * @returns A **new** `PageObjectModel` with updated `width`, `height`, `x_axis`, and
 * `y_axis`, ensuring the fixed corner remains in place
 */
function adjustObjectForSnapping(
	newWidth: number,
	newHeight: number,
	baseObject: PageObjectModel,
	fixedCornerIndex: FixedCornerIndex,
	initialPoints: Point[],
): PageObjectModel {
	/**
	 * Create a temporary object with the new width and height, but keep the original position
	 */
	const newObject: PageObjectModel = {
		...baseObject,
		width: newWidth,
		height: newHeight,
	};
	/**
	 * Calculate the new rotated corners of this temporary object, this is necessary
	 * to determine how the fixed corner's position has changed due to resizing
	 */

	const newPoints = getRotatedCorners(newObject);
	/**
	 * Compute the difference between the original fixed corner's position and its
	 * new position after resizing.
	 * `differentialX` and `differentialY` represent how much the fixed corner has moved
	 */
	const differentialX = initialPoints[fixedCornerIndex].x - newPoints[fixedCornerIndex].x;
	const differentialY = initialPoints[fixedCornerIndex].y - newPoints[fixedCornerIndex].y;
	/**
	 * Adjust the object's position by the differential to counteract the movement
	 * of the fixed corner, ensuring it remains in its original location
	 */
	const newXAxis = baseObject.x_axis + differentialX;
	const newYAxis = baseObject.y_axis + differentialY;

	/**
	 * Return a new object with the updated dimensions and adjusted position, this
	 * ensures the fixed corner stays anchored while the object is resized
	 */
	return {
		...newObject,
		x_axis: newXAxis,
		y_axis: newYAxis,
	};
}

/**
 * Snap a scalar coordinate to the nearest magnetic band, if the point
 * lies *inside* one of the bands.
 * @param value The raw coordinate to test (page units)
 * @param lines Magnetic bands expressed as `{min, max}` in page units
 * @param offset Optional offset to subtract from the result.
 * Used when snapping the *far* edge so that the object
 * keeps its size after the snap
 * @returns The (possibly) corrected coordinate.
 * If no band was hit, returns the original `value`.
 */
function applySnapping(
	value: number,
	lines: MagneticBand[],
	offset: number = 0,
): number {
	for (const line of lines) {
		const {
			min,
			max,
		} = line;

		if (
			value >= min
			&& value <= max
		) {
			const center = (min + max) / 2;

			return (
				value < center
					? min - offset
					: max - offset
			);
		}
	}

	return value;
}

/**
 * Produce a *new* PageObjectModel representing the object after applying
 * a proportional resize defined by `deltaWidth/Height × resizeFactor`.
 *
 * The helper enforces:
 * 		- min-size limit (`minSizeLimit`)
 * 		- optional aspect-ratio lock
 * 		- photo-crop constraints against the active source bounds
 * 		- anchor correction so that `fixedCornerIndex` stays in place
 * @param deltaWidth Signed Δwidth in local coordinates
 * @param deltaHeight Signed Δheight in local coordinates
 * @param initialObject The starting object state
 * @param options.aspectRatio Locked aspect ratio or `null`.
 * @param options.fixedCornerIndex Corner/side that must remain fixed
 * @param options.initialPoints Rotated corners of `initialObject`
 * @param options.minSizeLimit Absolute minimum size in page units
 * @param options.resizeFactor Factor ∈ [0,1] chosen by binary search
 * @param options.resizeSide Handle being dragged (bottom-left, …)
 * @returns A **new** object model with updated geometry + crop
 */
function calculateObjectResize(
	deltaWidth: number,
	deltaHeight: number,
	initialObject: PageObjectModel,
	options: CalculateObjectResizeOptions,
): PageObjectModel {
	const {
		cropheight: initialCropHeight,
		cropwidth: initialCropWidth,
		cropx: initialCropX,
		cropy: initialCropY,
		height: initialHeight,
		type,
		width: initialWidth,
		x_axis: initialXAxis,
		y_axis: initialYAxis,
	} = initialObject;
	const {
		aspectRatio,
		fixedCornerIndex,
		initialPoints,
		minSizeLimit,
		maxSizeLimit,
		resizeFactor,
		resizeSide,
	} = options;
	const isPhoto = type === 'photo';
	const resizeDirection = (
		resizeSide
			? resizeParameters[resizeSide]
			: undefined
	);
	const enforceMinWidth = (
		!resizeDirection
		|| resizeDirection.widthDirection !== 0
	);
	const enforceMinHeight = (
		!resizeDirection
		|| resizeDirection.heightDirection !== 0
	);
	const cropBounds = getPageObjectCropBounds(initialObject);
	let newCropHeight = initialCropHeight;
	let newCropWidth = initialCropWidth;
	let newCropX = initialCropX;
	let newCropY = initialCropY;
	let newHeight = initialHeight + resizeFactor * deltaHeight;
	let newWidth = initialWidth + resizeFactor * deltaWidth;
	const fixedPoint = initialPoints[fixedCornerIndex];

	if (aspectRatio) {
		/**
		 * Photo from corner side handles:
		 * - Width and height scale together via `aspectRatio`
		 * - Crop dimensions unchanged (inherited from the initial values)
		 */
		newHeight = newWidth * aspectRatio;

		if (newHeight < minSizeLimit) {
			newHeight = minSizeLimit;
			newWidth = newHeight / aspectRatio;
		}
		if (newWidth < minSizeLimit) {
			newWidth = minSizeLimit;
			newHeight = newWidth * aspectRatio;
		}

		if (
			typeof maxSizeLimit === 'number'
			&& newHeight > maxSizeLimit
		) {
			newHeight = maxSizeLimit;
			newWidth = newHeight / aspectRatio;
		}
		if (
			typeof maxSizeLimit === 'number'
			&& newWidth > maxSizeLimit
		) {
			newWidth = maxSizeLimit;
			newHeight = newWidth * aspectRatio;
		}
	} else {
		/**
		 * Non-Photo or Side Handles:
		 * - Width and height adjust independently
		 * - Constrained by `minSizeLimit` and `maxSizeLimit`
		 */
		if (
			enforceMinWidth
			&& newWidth < minSizeLimit
		) {
			newWidth = minSizeLimit;
		}
		if (
			enforceMinHeight
			&& newHeight < minSizeLimit
		) {
			newHeight = minSizeLimit;
		}

		if (
			typeof maxSizeLimit === 'number'
			&& newWidth > maxSizeLimit
		) {
			newWidth = maxSizeLimit;
		}
		if (
			typeof maxSizeLimit === 'number'
			&& newHeight > maxSizeLimit
		) {
			newHeight = maxSizeLimit;
		}

		/**
		 * For photo side handles:
		 * - Scale crop dimensions proportionally
		 * - Adjust crop positions/axis
		 * - Constrained by active source bounds
		 */
		if (isPhoto) {
			if (resizeSide === 'right') {
				newCropWidth = initialCropWidth * (newWidth / initialWidth);

				if (newCropWidth > cropBounds.maxX - initialCropX) {
					newCropWidth = cropBounds.maxX - initialCropX;
					newWidth = initialWidth * (newCropWidth / initialCropWidth);
				}
			} else if (resizeSide === 'left') {
				newCropWidth = initialCropWidth * (newWidth / initialWidth);
				newCropX = initialCropX + (initialCropWidth - newCropWidth);

				if (newCropX < cropBounds.minX) {
					newCropX = cropBounds.minX;
					newCropWidth = initialCropX + initialCropWidth - cropBounds.minX;
					newWidth = initialWidth * (newCropWidth / initialCropWidth);
				}
			} else if (resizeSide === 'bottom') {
				newCropHeight = initialCropHeight * (newHeight / initialHeight);

				if (newCropHeight > cropBounds.maxY - initialCropY) {
					newCropHeight = cropBounds.maxY - initialCropY;
					newHeight = initialHeight * (newCropHeight / initialCropHeight);
				}
			} else if (resizeSide === 'top') {
				newCropHeight = initialCropHeight * (newHeight / initialHeight);
				newCropY = initialCropY + (initialCropHeight - newCropHeight);

				if (newCropY < cropBounds.minY) {
					newCropY = cropBounds.minY;
					newCropHeight = initialCropY + initialCropHeight - cropBounds.minY;
					newHeight = initialHeight * (newCropHeight / initialCropHeight);
				}
			}
		}
	}

	const newObject: PageObjectModel = {
		...initialObject,
		cropheight: Math.round(newCropHeight),
		cropwidth: Math.round(newCropWidth),
		cropx: Math.round(newCropX),
		cropy: Math.round(newCropY),
		height: Math.round(newHeight),
		width: Math.round(newWidth),
	};

	/**
	 * Fix the opposite corner/side using `fixedCornerIndex`,
	 * which keeps the object anchored correctly
	 */

	const newPoints = getRotatedCorners(newObject);
	const differentialX = fixedPoint.x - newPoints[fixedCornerIndex].x;
	const differentialY = fixedPoint.y - newPoints[fixedCornerIndex].y;
	const newXAxis = initialXAxis + differentialX;
	const newYAxis = initialYAxis + differentialY;

	return {
		...newObject,
		x_axis: Math.round(newXAxis),
		y_axis: Math.round(newYAxis),
	};
}

/**
 * Adjust the object's position to ensure at least 50 pixels are visible within the page.
 * @param objectModel The object model to adjust
 * @param pageModel The page model defining the bounds
 * @param minVisiblePixels Minimum number of pixels that must be visible per dimension before clamping to the object's bounding box (default 50)
 * @returns A new PageObjectModel with adjusted position
 */
function ensureMinimumVisiblePixels(
	objectModel: PageObjectModel,
	pageModel: PageModel,
	minVisiblePixels: number = 50,
): PageObjectModel {
	/**
	 * Clamp the visibility requirement so it never exceeds the current bounding box.
	 */
	const boundingBox = getBoundingBox(getRotatedCorners(objectModel));
	const boundingBoxHeight = boundingBox.maxY - boundingBox.minY;
	const boundingBoxWidth = boundingBox.maxX - boundingBox.minX;
	const minVisibleHeight = Math.min(
		minVisiblePixels,
		boundingBoxHeight,
	);
	const minVisibleWidth = Math.min(
		minVisiblePixels,
		boundingBoxWidth,
	);

	if (

		!hasMinimumVisiblePixels(
			objectModel,
			pageModel,
			minVisiblePixels,
		)
	) {
		let {
			x_axis: newXAxis,
			y_axis: newYAxis,
		} = objectModel;

		// Calculate visible dimensions
		const visibleMinX = Math.max(
			boundingBox.minX,
			0,
		);
		const visibleMaxX = Math.min(
			boundingBox.maxX,
			pageModel.width,
		);
		const visibleWidth = visibleMaxX - visibleMinX;
		const visibleMinY = Math.max(
			boundingBox.minY,
			0,
		);
		const visibleMaxY = Math.min(
			boundingBox.maxY,
			pageModel.height,
		);
		const visibleHeight = visibleMaxY - visibleMinY;

		// Adjust X position
		if (visibleWidth < minVisibleWidth) {
			if (boundingBox.maxX < minVisibleWidth) {
				// Too far left, shift right
				newXAxis += minVisibleWidth - boundingBox.maxX;
			} else if (boundingBox.minX > pageModel.width - minVisibleWidth) {
				// Too far right, shift left
				newXAxis -= (boundingBox.minX - (pageModel.width - minVisibleWidth));
			}
		}

		// Adjust Y position
		if (visibleHeight < minVisibleHeight) {
			if (boundingBox.maxY < minVisibleHeight) {
				// Too far up, shift down
				newYAxis += minVisibleHeight - boundingBox.maxY;
			} else if (boundingBox.minY > pageModel.height - minVisibleHeight) {
				// Too far down, shift up
				newYAxis -= (boundingBox.minY - (pageModel.height - minVisibleHeight));
			}
		}

		return {
			...objectModel,
			x_axis: newXAxis,
			y_axis: newYAxis,
		};
	}

	return objectModel;
}

/**
 * Fix the bounds of an object model to ensure that the crop values are valid.
 * @param objectModel The object model to fix
 * @returns The fixed object model
 */
function fixObjectBounds(objectModel: PageObjectModel): PageObjectModel {
	const cropBounds = getPageObjectCropBounds(objectModel);
	let {
		cropheight: newCropHeight,
		cropwidth: newCropWidth,
		cropx: newCropX,
		cropy: newCropY,
		height: newHeight,
		width: newWidth,
	} = objectModel;

	/**
	 * Adjust `cropheight`, `cropwidth`, `cropx` and `cropy` if they are out of bounds
	 */
	if (newCropX < cropBounds.minX) {
		/**
		 * Shift cropx to the right and adjust the width to maintain ratio.
		 */
		newWidth += newCropX - cropBounds.minX;
		newCropX = cropBounds.minX;
	}
	if (newCropY < cropBounds.minY) {
		/**
		 * Shift cropy to the bottom and adjust the height to maintain ratio.
		 */
		newHeight += newCropY - cropBounds.minY;
		newCropY = cropBounds.minY;
	}
	if ((newCropY + newCropHeight) > cropBounds.maxY) {
		/**
		 * Shift cropheight to the top and adjust the height to maintain ratio
		 */
		newHeight += (newCropY + newCropHeight) - cropBounds.maxY;
		newCropHeight = (cropBounds.maxY - newCropY);
	}
	if ((newCropX + newCropWidth) > cropBounds.maxX) {
		/**
		 * Shift cropwidth to the left and adjust the width to maintain ratio
		 */
		newWidth += (newCropX + newCropWidth) - cropBounds.maxX;
		newCropWidth = (cropBounds.maxX - newCropX);
	}

	const normalizedCrop = normalizeCropRect(
		{
			cropheight: newCropHeight,
			cropwidth: newCropWidth,
			cropx: newCropX,
			cropy: newCropY,
		},
		cropBounds,
	);

	return {
		...objectModel,
		...normalizedCrop,
		height: Math.round(newHeight),
		width: Math.round(newWidth),
	};
}

/**
 * Compute the active alignment guides for the supplied object model.
 * @param objectModel Object to analyse in page coordinates
 * @param pageMagneticLines Precomputed magnetic lines for the page
 * @param tolerance Default tolerance threshold applied to guide checks
 * @returns Array of guides that are currently active (the object is aligned with them)
 */
function getActiveSnapGuides(
	objectModel: PageObjectModel,
	pageMagneticLines: PageMagneticLines,
	tolerance: number,
	options?: GetActiveSnapGuidesOptions,
): SnapGuides {
	const boundingBox = getBoundingBox(
		getRotatedCorners(objectModel),
	);
	const guides: SnapGuides = [];
	const { excludeGuide } = (
		options
		?? {}
	);

	const collectGuides = (
		axisGuides: MagneticGuide[],
		orientation: SnapGuideOrientation,
	): void => {
		for (const guide of axisGuides) {
			if (
				excludeGuide?.(
					guide,
					orientation,
				)
			) {
				continue;
			}

			const guideTolerance = guide.tolerance ?? tolerance;
			/**
			 * Resolve all anchor positions that this guide should align against.
			 */
			const values = getGuideTargetValues(
				guide.target,
				orientation,
				boundingBox,
			);

			if (
				values.some((value) => (
					Math.abs(value - guide.position) <= guideTolerance
				))
			) {
				guides.push({
					orientation,
					position: guide.position,
					type: guide.type,
				});
			}
		}
	};

	collectGuides(
		pageMagneticLines.x.guides,
		'vertical',
	);
	collectGuides(
		pageMagneticLines.y.guides,
		'horizontal',
	);

	return guides;
}

/**
 * Calculate the bounding box of rotated points
 * @param points Array of points
 * @returns The bounding box of the points
 */
function getBoundingBox(points: Point[]): BoundingBox {
	const xs = points.map((p) => p.x);
	const ys = points.map((p) => p.y);

	return {
		minX: Math.min(...xs),
		maxX: Math.max(...xs),
		minY: Math.min(...ys),
		maxY: Math.max(...ys),
	};
}

/**
 * Derive the capture radius for centre-line snapping based on viewport scaling.
 * The base tolerance is defined in screen pixels and converted back into page units.
 * @param scaling Viewport-to-page scaling ratio
 * @returns Snap tolerance in page units
 */
function getCenterSnapTolerance(scaling: number): number {
	const normalizedScaling = Math.max(
		scaling,
		Number.EPSILON,
	);
	const screenTolerance = GUIDE_SNAP_BASE_TOLERANCE / normalizedScaling;

	return Math.max(
		screenTolerance,
		GUIDE_SNAP_BASE_TOLERANCE,
	);
}

/**
 * Determines the point (corner or side midpoint) being dragged during resizing
 * based on the specified `resizeSide`. This point is used for snapping to
 * magnetic bands.
 *
 * - For **corner handles** (e.g., `'top-left'`), returns the corresponding corner
 * - For **side handles** (e.g., `'top'`, `'left'`), returns the midpoint of the
 *   dragged side, calculated as the average of the two adjacent corners
 *
 * @param objectModel The current object state.
 * @param resizeSide The handle being dragged (e.g., `'top-left'`, `'bottom'`, `'right'`).
 * @returns A `Point` representing the dragged corner or side midpoint in page coordinates
 */
function getDraggedPoint(
	objectModel: PageObjectModel,
	resizeSide: EditorInteractivePageObjectResizeSide,
): Point {
	/**
	 * Retrieve the rotated corner points of the object, these points are used to
	 * identify or calculate the dragged point
	 */

	const points = getRotatedCorners(objectModel);

	/**
	 * Determine the dragged point based on the `resizeSide`.
	 *
	 * For corner handles, directly return the corresponding corner point.
	 * For side handles, calculate the midpoint of the dragged side.
	 */
	if (resizeSide === 'bottom-left') {
		return points[3];
	}
	if (resizeSide === 'bottom') {
		return {
			x: (points[2].x + points[3].x) / 2,
			y: (points[2].y + points[3].y) / 2,
		};
	}
	if (resizeSide === 'bottom-right') {
		return points[2];
	}
	if (resizeSide === 'left') {
		return {
			x: (points[0].x + points[3].x) / 2,
			y: (points[0].y + points[3].y) / 2,
		};
	}
	if (resizeSide === 'right') {
		return {
			x: (points[1].x + points[2].x) / 2,
			y: (points[1].y + points[2].y) / 2,
		};
	}
	if (resizeSide === 'top-left') {
		return points[0];
	}
	if (resizeSide === 'top') {
		return {
			x: (points[0].x + points[1].x) / 2,
			y: (points[0].y + points[1].y) / 2,
		};
	}

	return points[1];
}

/**
 * Resolves the insertion index for a backend-only fixed page.
 *
 * @param insertionBaseIndex Index representing the end of the printable inside pages.
 * @param serialnumber Serial offset from the end (`-1` = last inside page).
 * @returns Safe array index where the fixed page must be inserted.
 */
function getFixedPageInsertionIndex(
	insertionBaseIndex: number,
	serialnumber: number,
): number {
	return Math.max(
		0,
		Math.min(
			insertionBaseIndex,
			insertionBaseIndex + serialnumber + 1,
		),
	);
}

/**
 * Resolve the list of page-space anchor coordinates associated with the supplied
 * guide target(s).
 * @param target Guide target or targets (`start`/`center`/`end`) that define
 * the anchor positions to align.
 * @param orientation Axis orientation the guide applies to.
 * @param boundingBox Bounding box of the object in page space.
 * @returns Ordered list of anchor coordinates expressed in page units.
 */
function getGuideTargetValues(
	target: MagneticGuideTarget | MagneticGuideTarget[],
	orientation: SnapGuideOrientation,
	boundingBox: BoundingBox,
): number[] {
	const targets = (
		Array.isArray(target)
			? target
			: [target]
	);

	/**
	 * Convert each target to its numeric anchor(s).
	 */
	return targets.flatMap((currentTarget) => {
		if (currentTarget === 'center') {
			return [
				orientation === 'vertical'
					? (boundingBox.minX + boundingBox.maxX) / 2
					: (boundingBox.minY + boundingBox.maxY) / 2,
			];
		}

		if (currentTarget === 'start') {
			return [
				orientation === 'vertical'
					? boundingBox.minX
					: boundingBox.minY,
			];
		}

		if (currentTarget === 'end') {
			return [
				orientation === 'vertical'
					? boundingBox.maxX
					: boundingBox.maxY,
			];
		}

		return [];
	});
}

/**
 * Build the two magnetic-band arrays (horizontal **x**, vertical **y**)
 * from the page geometry and UI margins. Extend the `guides` lists here when
 * new snap targets (e.g. thirds gridlines) are required. A guide can reference
 * multiple anchors by passing an array of `target` values.
 * @param pageModel Page width / height in page units
 * @param bleedMargin Thickness of the outer bleed snap band
 * @param pageMargin Thickness of the inner page-margin band
 * @param scaling Viewport-to-page scaling ratio
 * @returns `{ x: MagneticAxisLines, y: MagneticAxisLines }`
 */
function getPageMagneticLines(
	pageModel: PageModel,
	bleedMargin: number,
	pageMargin: number,
	scaling: number,
): PageMagneticLines {
	const edgeTolerance = Math.max(
		1,
		(
			Math.min(
				pageMargin,
				bleedMargin,
			)
			|| (GUIDE_SNAP_BASE_TOLERANCE / scaling)
		),
	);

	return {
		x: {
			bands: [
				{
					min: -bleedMargin,
					max: pageMargin,
				},
				{
					min: pageModel.width - pageMargin,
					max: pageModel.width + bleedMargin,
				},
			],
			guides: [
				{
					position: 0,
					target: 'start',
					tolerance: edgeTolerance,
					type: 'edge',
				},
				{
					position: pageModel.width,
					target: 'end',
					tolerance: edgeTolerance,
					type: 'edge',
				},
				{
					position: pageModel.width / 2,
					target: [
						'start',
						'center',
						'end',
					],
					type: 'center',
				},
			],
		},
		y: {
			bands: [
				{
					min: -bleedMargin,
					max: pageMargin,
				},
				{
					min: pageModel.height - pageMargin,
					max: pageModel.height + bleedMargin,
				},
			],
			guides: [
				{
					position: 0,
					target: 'start',
					tolerance: edgeTolerance,
					type: 'edge',
				},
				{
					position: pageModel.height,
					target: 'end',
					tolerance: edgeTolerance,
					type: 'edge',
				},
				{
					position: pageModel.height / 2,
					target: [
						'start',
						'center',
						'end',
					],
					type: 'center',
				},
			],
		},
	};
}

/**
 * Get the rotated corners of an object model
 * @param objectModel The object model to get corners from
 * @returns The rotated corners of the object model
 */
function getRotatedCorners(objectModel: OptionalExceptFor<PageObjectModel, 'height' | 'rotate' | 'width' | 'x_axis' | 'y_axis' >): Point[] {
	const {
		x_axis,
		y_axis,
		width,
		height,
		rotate,
	} = objectModel;
	const cx = x_axis + width / 2;
	const cy = y_axis + height / 2;
	const points = [
		{
			x: x_axis,
			y: y_axis,
		}, // top-left (0)
		{
			x: x_axis + width,
			y: y_axis,
		}, // top-right (1)
		{
			x: x_axis + width,
			y: y_axis + height,
		}, // bottom-right (2)
		{
			x: x_axis,
			y: y_axis + height,
		}, // bottom-left (3)
	];

	return points.map((p) => (

		rotatePoint(
			cx,
			cy,
			p.x,
			p.y,
			rotate,
		)
	));
}

/**
 * Check if at least 50 pixels of the object's width and height are visible within the page.
 * @param objectModel The object model to check
 * @param pageModel The page model to check against
 * @param minVisiblePixels Minimum number of pixels that must be visible per dimension before clamping to the object's bounding box (default to {@link MINIMUM_VISIBLE_PIXELS})
 * @returns True if at least minVisiblePixels are visible in both dimensions, false otherwise
 */
function hasMinimumVisiblePixels(
	objectModel: PageObjectModel,
	pageModel: PageModel,
	minVisiblePixels = MINIMUM_VISIBLE_PIXELS,
): boolean {
	const boundingBox = getBoundingBox(getRotatedCorners(objectModel));
	const boundingBoxHeight = boundingBox.maxY - boundingBox.minY;
	const boundingBoxWidth = boundingBox.maxX - boundingBox.minX;
	const minVisibleHeight = Math.min(
		minVisiblePixels,
		boundingBoxHeight,
	);
	const minVisibleWidth = Math.min(
		minVisiblePixels,
		boundingBoxWidth,
	);
	const visibleMinX = Math.max(
		boundingBox.minX,
		0,
	);
	const visibleMaxX = Math.min(
		boundingBox.maxX,
		pageModel.width,
	);
	const visibleWidth = visibleMaxX - visibleMinX;
	const visibleMinY = Math.max(
		boundingBox.minY,
		0,
	);
	const visibleMaxY = Math.min(
		boundingBox.maxY,
		pageModel.height,
	);
	const visibleHeight = visibleMaxY - visibleMinY;

	return (
		visibleWidth >= minVisibleWidth
		&& visibleHeight >= minVisibleHeight
	);
}

/**
 * Checks whether backend-only fixed pages should be applied for the offering.
 *
 * @param offeringModel Offering to inspect.
 * @returns True when the offering belongs to the `BookTypes` group.
 */
function isBookTypeOffering(
	offeringModel: OfferingModel | null | undefined,
): offeringModel is OfferingModel {
	return Boolean(
		offeringModel
		&& OfferingGroups(
			offeringModel.groupid,
			'BookTypes',
		),
	);
}

/**
 * Rotate a point around a center point by a given angle
 * @param cx Center x coordinate
 * @param cy Center y coordinate
 * @param px Point x coordinate
 * @param py Point y coordinate
 * @param angle Angle in degrees
 * @returns Rotated point
 */
function rotatePoint(
	cx: number,
	cy: number,
	px: number,
	py: number,
	angle: number,
): Point {
	if (angle === 0) {
		return {
			x: px,
			y: py,
		};
	}

	const rad = angle * DEGREES_TO_RADIANS;
	const cos = Math.cos(rad);
	const sin = Math.sin(rad);
	const dx = px - cx;
	const dy = py - cy;

	return {
		x: cx + dx * cos - dy * sin,
		y: cy + dx * sin + dy * cos,
	};
}

/**
 * Scales a coordinate that uses the trim origin (0,0) while accounting for bleed segments
 * that may change independently from the trim size.
 *
 * @param value Coordinate to transform from the source space.
 * @param sourceDimension Trim dimension (width or height) of the source page.
 * @param sourceBleed Bleed margin of the source page.
 * @param targetDimension Trim dimension (width or height) of the target page.
 * @param targetBleed Bleed margin of the target page.
 * @returns Coordinate expressed in the target page space.
 */
function scaleAxisCoordinate(
	value: number,
	sourceDimension: number,
	sourceBleed: number,
	targetDimension: number,
	targetBleed: number,
): number {
	/**
	 * Negative or zero coordinates live inside the bleed region, so we keep
	 * them as a proportional offset of the bleed thickness rather than
	 * scaling them against the trim width/height.
	 */
	if (value <= 0) {
		if (
			!sourceBleed
			|| !targetBleed
		) {
			return 0;
		}

		return (value / sourceBleed) * targetBleed;
	}

	/**
	 * Coordinates beyond the trim edge represent the trailing bleed margin.
	 * We convert the overflow (distance past the trim) so it matches the
	 * destination bleed thickness.
	 */
	if (value >= sourceDimension) {
		const overflow = value - sourceDimension;

		if (
			!sourceBleed
			|| !targetBleed
		) {
			return targetDimension;
		}

		return targetDimension + (overflow / sourceBleed) * targetBleed;
	}

	/**
	 * Points inside the trim area scale proportionally using the trim sizes
	 * because they do not interact with bleed adjustments.
	 */
	return (value / sourceDimension) * targetDimension;
}

/**
 * Snap an object being **moved** (no size change) so its edges line up
 * with the nearest magnetic bands.
 *
 * When both X edges (or Y edges) are inside bands simultaneously the
 * smaller absolute shift wins to avoid ambiguous snaps.
 * @param objectModel The object after applying the raw move delta
 * @param pageModel Page size in page units
 * @param options.bleedMargin Thickness of the outer bleed snap band
 * @param options.pageMargin Thickness of the inner page-margin band
 * @returns A new `PageObjectModel` with `x_axis / y_axis` adjusted
 */
function snapMovedObjectModel(
	objectModel: PageObjectModel,
	pageModel: PageModel,
	options: SnapMovedObjectOptions,
): SnapMovedObjectResult {
	const {
		bleedMargin,
		pageMargin,
		scaling,
	} = options;
	let {
		x_axis: newXAxis,
		y_axis: newYAxis,
	} = objectModel;
	/**
	 * Calculate the object’s current bounding box in **page space**.
	 * All snapping decisions are made on these global coordinates.
	 */
	let boundingBox = getBoundingBox(getRotatedCorners(objectModel));
	/**
	 * Build the magnetic-band arrays for both axes once per call.
	 * Each band is expressed as {min, max} in page units.
	 */
	const pageMagneticLines = getPageMagneticLines(
		pageModel,
		bleedMargin,
		pageMargin,
		scaling,
	);
	const horizontalBands = pageMagneticLines.x.bands;
	const verticalBands = pageMagneticLines.y.bands;
	let bandRangeX: MagneticBand | undefined;
	let bandRangeY: MagneticBand | undefined;
	const centerSnapTolerance = getCenterSnapTolerance(scaling);
	const orientedGuides: Record<'x' | 'y', OrientedMagneticGuide[]> = {
		x: pageMagneticLines.x.guides.map((guide) => ({
			...guide,
			orientation: 'vertical',
		})),
		y: pageMagneticLines.y.guides.map((guide) => ({
			...guide,
			orientation: 'horizontal',
		})),
	};

	/**
	 * Snap left edge (minX)
	 */
	const snappedLeft = applySnapping(
		boundingBox.minX,
		pageMagneticLines.x.bands,
	);
	/**
	 * Snap right edge (maxX)
	 */
	const snappedRight = applySnapping(
		boundingBox.maxX,
		pageMagneticLines.x.bands,
		boundingBox.maxX - boundingBox.minX,
	);
	/**
	 * Snap top edge (minY)
	 */
	const snappedTop = applySnapping(
		boundingBox.minY,
		pageMagneticLines.y.bands,
	);
	/**
	 * Snap bottom edge (maxY)
	 */
	const snappedBottom = applySnapping(
		boundingBox.maxY,
		pageMagneticLines.y.bands,
		boundingBox.maxY - boundingBox.minY,
	);

	/**
	 * Compute independent snap shifts for every edge:
	 * 		- `shiftXLeft`: if left edge entered a band, move by ΔX
	 * 		- `shiftXRight`: same for right edge (offset keeps width)
	 * 		- `shiftYTop`: if top edge entered a band, move by ΔY
	 * 		- `shiftYBottom`: same for bottom edge (offset keeps height)
	 */
	const shiftCandidatesX: ShiftCandidate[] = [];
	const shiftCandidatesY: ShiftCandidate[] = [];

	if (snappedLeft !== boundingBox.minX) {
		shiftCandidatesX.push({
			bandRange: horizontalBands[0],
			shift: snappedLeft - boundingBox.minX,
		});
	}

	if (snappedRight !== boundingBox.maxX) {
		shiftCandidatesX.push({
			bandRange: horizontalBands[1],
			shift: snappedRight - boundingBox.minX,
		});
	}

	if (snappedTop !== boundingBox.minY) {
		shiftCandidatesY.push({
			bandRange: verticalBands[0],
			shift: snappedTop - boundingBox.minY,
		});
	}

	if (snappedBottom !== boundingBox.maxY) {
		shiftCandidatesY.push({
			bandRange: verticalBands[1],
			shift: snappedBottom - boundingBox.minY,
		});
	}

	const selectBestCandidate = (
		candidates: ShiftCandidate[],
	): ShiftCandidate | undefined => {
		let best: ShiftCandidate | undefined;

		for (let index = 0; index < candidates.length; index += 1) {
			const candidate = candidates[index];

			if (
				!best
				|| Math.abs(candidate.shift) < Math.abs(best.shift)
			) {
				best = candidate;
			}
		}

		return best;
	};
	const pushGuideCandidates = (
		candidates: ShiftCandidate[],
		guides: OrientedMagneticGuide[],
	): void => {
		for (const guide of guides) {
			const guideTolerance = (
				guide.tolerance
				?? centerSnapTolerance
			);
			/**
			 * Evaluate every anchor for this guide and enqueue matching shifts.
			 */
			const values = getGuideTargetValues(
				guide.target,
				guide.orientation,
				boundingBox,
			);

			for (const value of values) {
				const distance = guide.position - value;

				if (Math.abs(distance) <= guideTolerance) {
					candidates.push({
						guide,
						shift: distance,
					});
				}
			}
		}
	};

	const hasBandCandidateX = shiftCandidatesX.length > 0;
	const hasBandCandidateY = shiftCandidatesY.length > 0;

	if (
		hasBandCandidateX
		|| hasBandCandidateY
	) {
		/**
		 * Prefer magnetic bands per axis; guides only resolve the axes that remain
		 * free after band snapping.
		 */
		const bestBandShiftX = (
			hasBandCandidateX
				? selectBestCandidate(shiftCandidatesX)
				: undefined
		);
		const bestBandShiftY = (
			hasBandCandidateY
				? selectBestCandidate(shiftCandidatesY)
				: undefined
		);

		if (bestBandShiftX) {
			newXAxis += bestBandShiftX.shift;
			bandRangeX = bestBandShiftX.bandRange;
			shiftCandidatesX.length = 0;
		}

		if (bestBandShiftY) {
			newYAxis += bestBandShiftY.shift;
			bandRangeY = bestBandShiftY.bandRange;
			shiftCandidatesY.length = 0;
		}

		if (
			bestBandShiftX
			|| bestBandShiftY
		) {
			objectModel = {
				...objectModel,
				x_axis: newXAxis,
				y_axis: newYAxis,
			};
			boundingBox = getBoundingBox(getRotatedCorners(objectModel));
		}

		if (
			bestBandShiftX
			&& bestBandShiftY
		) {
			return {
				centerSnapTolerance,
				objectModel,
				pageMagneticLines,
				snapGuides: getActiveSnapGuides(
					objectModel,
					pageMagneticLines,
					centerSnapTolerance,
					{
						excludeGuide: (
							guide,
							orientation,
						) => !!(
							(
								orientation === 'vertical'
								&& bandRangeX
								&& valuesIntersectBand(
									guide,
									'vertical',
									bandRangeX,
									boundingBox,
									centerSnapTolerance,
								)
							)
							|| (
								orientation === 'horizontal'
								&& bandRangeY
								&& valuesIntersectBand(
									guide,
									'horizontal',
									bandRangeY,
									boundingBox,
									centerSnapTolerance,
								)
							)
						),
					},
				),
			};
		}
	}

	if (!bandRangeX) {
		pushGuideCandidates(
			shiftCandidatesX,
			orientedGuides.x,
		);
	}

	if (!bandRangeY) {
		pushGuideCandidates(
			shiftCandidatesY,
			orientedGuides.y,
		);
	}

	const bestShiftX = selectBestCandidate(shiftCandidatesX);
	const bestShiftY = selectBestCandidate(shiftCandidatesY);

	if (bestShiftX) {
		newXAxis += bestShiftX.shift;
	}
	if (bestShiftY) {
		newYAxis += bestShiftY.shift;
	}

	const snappedObjectModel: PageObjectModel = {
		...objectModel,
		x_axis: newXAxis,
		y_axis: newYAxis,
	};
	const snappedBoundingBox = getBoundingBox(
		getRotatedCorners(snappedObjectModel),
	);
	const snapGuides = getActiveSnapGuides(
		snappedObjectModel,
		pageMagneticLines,
		centerSnapTolerance,
		{
			excludeGuide: (
				guide,
				orientation,
			) => {
				if (
					orientation === 'vertical'
					&& bandRangeX
					&& valuesIntersectBand(
						guide,
						'vertical',
						bandRangeX,
						snappedBoundingBox,
						centerSnapTolerance,
					)
				) {
					return true;
				}

				if (
					orientation === 'horizontal'
					&& bandRangeY
					&& valuesIntersectBand(
						guide,
						'horizontal',
						bandRangeY,
						snappedBoundingBox,
						centerSnapTolerance,
					)
				) {
					return true;
				}

				return false;
			},
		},
	);

	/**
	 * Return a *new* object instance with the corrected position and metadata.
	 */
	return {
		centerSnapTolerance,
		objectModel: snappedObjectModel,
		pageMagneticLines,
		snapGuides,
	};
}

/**
 * Snap an object being **resized** so that the currently dragged corner or side
 * aligns with the nearest magnetic band.
 *
 * Behaviour by object/handle type:
 * 		- **Text / free‑aspect objects** – width and height may change independently
 * 		- **Photo + corner handle** – aspect ratio is locked; only the width change is
 * 			solved analytically (height follows `aspect = h / w`)
 * 		- **Photo + side handle** – width *or* height scales the crop rectangle. If the
 * 			crop would outrun the bitmap bounds the snap is *saturated* at the
 * 			maximum pixel area available instead of being refused
 *
 * In all cases the helper keeps the opposite corner / side (given by
 * `resizeParameters[resizeSide].fixedCornerIndex`) perfectly anchored, taking the
 * object’s current rotation into account.
 *
 * @param objectModel The object state **after** applying the raw resize delta
 * @param pageModel Page size in page units
 * @param options.bleedMargin Thickness of the outer bleed snap band
 * @param options.minSizeLimit Minimum size limit for the object
 * @param options.pageMargin Thickness of the inner page‑margin band
 * @param options.resizeSide Handle being dragged (e.g. `'bottom'`, `'top‑left'`),
 * or `undefined` if the resize is from the center (pinch-to-zoom use case)
 * @returns A **new** `PageObjectModel` with width/height/crop/x_axis/y_axis
 * adjusted so that the dragged edge snaps, or the original `objectModel`
 * if no snap occurred this frame
 */
function snapResizedObjectModel(
	objectModel: PageObjectModel,
	pageModel: PageModel,
	options: SnapResizedObjectOptions,
): PageObjectModel {
	const {
		bleedMargin,
		minSizeLimit,
		pageMargin,
		resizeSide,
		scaling,
	} = options;
	/**
	 * Construct magnetic bands for X and Y axes based on page geometry and margins
	 */
	const pageMagneticLines = getPageMagneticLines(
		pageModel,
		bleedMargin,
		pageMargin,
		scaling,
	);

	/**
	 * Resize is happening from the center, pinch-to-zoom use case.
	 * TODO: re-visit this logic since it's not working properly for
	 * arbitrary angles (rotation % 90 !== 0)
	 */
	if (typeof resizeSide === 'undefined') {
		/**
		 * Get current geometry
		 */
		const centerX = objectModel.x_axis + objectModel.width / 2;
		const centerY = objectModel.y_axis + objectModel.height / 2;
		const boundingBox = getBoundingBox(getRotatedCorners(objectModel));
		const halfBoxHeight = (boundingBox.maxY - boundingBox.minY) / 2;
		const halfBoxWidth = (boundingBox.maxX - boundingBox.minX) / 2;

		/**
		 * Snap each edge *independently* to the nearest magnetic band
		 */
		const snappedLeft = applySnapping(
			boundingBox.minX,
			pageMagneticLines.x.bands,
		);
		const snappedRight = applySnapping(
			boundingBox.maxX,
			pageMagneticLines.x.bands,
		);
		const snappedTop = applySnapping(
			boundingBox.minY,
			pageMagneticLines.y.bands,
		);
		const snappedBottom = applySnapping(
			boundingBox.maxY,
			pageMagneticLines.y.bands,
		);

		/**
		 * Convert snapped edges into target half-sizes (keep centre fixed)
		 */
		const halfHeightCandidates: number[] = [];
		const halfWidthCandidates: number[] = [];

		if (snappedLeft !== boundingBox.minX) {
			halfWidthCandidates.push(centerX - snappedLeft);
		}
		if (snappedRight !== boundingBox.maxX) {
			halfWidthCandidates.push(snappedRight - centerX);
		}
		if (snappedTop !== boundingBox.minY) {
			halfHeightCandidates.push(centerY - snappedTop);
		}
		if (snappedBottom !== boundingBox.maxY) {
			halfHeightCandidates.push(snappedBottom - centerY);
		}

		/**
		 * Nothing entered a band -> return unchanged
		 */
		if (
			!halfWidthCandidates.length
			&& !halfHeightCandidates.length
		) {
			return objectModel;
		}

		/**
		 * Choose the *smallest* absolute change on each axis
		 */
		let targetHalfHeight = halfBoxHeight;
		let targetHalfWidth = halfBoxWidth;

		if (halfHeightCandidates.length) {
			targetHalfHeight = halfHeightCandidates.reduce(
				(best, candidate) => (
					Math.abs(candidate) < Math.abs(best)
						? candidate
						: best
				),
				halfHeightCandidates[0],
			);
		}

		if (halfWidthCandidates.length) {
			targetHalfWidth = halfWidthCandidates.reduce(
				(best, candidate) => (
					Math.abs(candidate) < Math.abs(best)
						? candidate
						: best
				),
				halfWidthCandidates[0],
			);
		}

		/**
		 * Enforce aspect lock (pinch always keeps ratio)
		 */
		const aspectRatio = objectModel.height / objectModel.width;

		/**
		 * Scale down whichever axis asked for the *smaller* relative change
		 */
		const scaleBoxWidth = targetHalfWidth / halfBoxWidth;
		const scaleBoxHeight = targetHalfHeight / halfBoxHeight;
		const scale = Math.min(
			scaleBoxWidth,
			scaleBoxHeight,
		);

		/**
		 * Apply that scale to the *local* width / height (keeps aspect)
		 */
		let newHeight = objectModel.height * scale;
		let newWidth = objectModel.width * scale;

		if (newWidth < minSizeLimit) {
			newWidth = minSizeLimit;
			newHeight = newWidth * aspectRatio;
		}

		if (newHeight < minSizeLimit) {
			newHeight = minSizeLimit;
			newWidth = newHeight / aspectRatio;
		}

		/**
		 * Build the snapped model directly (centre stays fixed)
		 */
		return {
			...objectModel,
			width: Math.round(newWidth),
			height: Math.round(newHeight),
			x_axis: Math.round(centerX - (newWidth / 2)),
			y_axis: Math.round(centerY - (newHeight / 2)),
		};
	}

	/**
	 * Retrieve the fixed corner index from resize parameters to anchor the opposite corner/s
	 */
	const { fixedCornerIndex } = resizeParameters[resizeSide];
	/**
	 * Compute the rotated corner positions of the object in page space for snapping decisions
	 */
	const initialRotatedPoints = getRotatedCorners(objectModel);

	/**
	 * Determine if the object is a photo for aspect ratio and crop handling
	 */
	const isPhoto = objectModel.type === 'photo';
	/**
	 * Check if the resize handle is a corner (affects both width and height)
	 */
	const isCornerHandle = [
		'top-left',
		'top-right',
		'bottom-left',
		'bottom-right',
	].includes(resizeSide);

	/**
	 * Handle photo objects resized from corner handles with locked aspect ratio
	 */
	if (
		isPhoto
		&& isCornerHandle
	) {
		/**
		 * 1. Identify the dragged corner’s position in page coordinates
		 */
		const draggedCorner = getDraggedPoint(
			objectModel,
			resizeSide,
		);

		/**
		 * 2. Attempt to snap the dragged corner's X and Y coordinates to magnetic bands
		 */
		const snappedXCoordinate = applySnapping(
			draggedCorner.x,
			pageMagneticLines.x.bands,
		);
		const snappedYCoordinate = applySnapping(
			draggedCorner.y,
			pageMagneticLines.y.bands,
		);
		/**
		 * Calculate the desired shift to align with the snapped position
		 */
		const snapDeltaX = snappedXCoordinate - draggedCorner.x;
		const snapDeltaY = snappedYCoordinate - draggedCorner.y;

		/**
		 * 3. Proceed only if snapping is needed (non-zero shift on either axis)
		 */
		if (
			snapDeltaX !== 0
			|| snapDeltaY !== 0
		) {
			/**
			 * 4. Calculate the aspect ratio to maintain proportional resizing
			 */
			const photoAspectRatio = objectModel.height / objectModel.width;

			/**
			 * 5. Use finite-difference to estimate how width changes affect the corner's position.
			 *    Perturb width and compute new height based on aspect ratio
			 */
			const objectWithExtraWidth = adjustObjectForSnapping(
				objectModel.width + epsilonPerturb,
				objectModel.height + epsilonPerturb * photoAspectRatio,
				objectModel,
				fixedCornerIndex,
				initialRotatedPoints,
			);
			const draggedCornerWithExtraWidth = getDraggedPoint(
				objectWithExtraWidth,
				resizeSide,
			);
			/**
			 * Compute derivatives ∂x/∂w and ∂y/∂w for the corner's movement
			 */
			const derivativeCornerXOverWidth = (draggedCornerWithExtraWidth.x - draggedCorner.x) / epsilonPerturb;
			const derivativeCornerYOverWidth = (draggedCornerWithExtraWidth.y - draggedCorner.y) / epsilonPerturb;

			/**
			 * 6. Solve for width adjustments to achieve snapping on X and/or Y
			 */
			const widthChangeCandidates: number[] = [];

			if (
				snapDeltaX !== 0
				&& Math.abs(derivativeCornerXOverWidth) > epsilonZero
			) {
				/**
				 * Calculate Δw needed to snap X coordinate
				 */
				widthChangeCandidates.push(snapDeltaX / derivativeCornerXOverWidth);
			}

			if (
				snapDeltaY !== 0
				&& Math.abs(derivativeCornerYOverWidth) > epsilonZero
			) {
				/**
				 * Calculate Δw needed to snap Y coordinate
				 */
				widthChangeCandidates.push(snapDeltaY / derivativeCornerYOverWidth);
			}

			if (widthChangeCandidates.length) {
				/**
				 * 7. Select the smallest |Δw| to minimize motion, mimicking "least motion wins"
				 */
				const deltaWidthChange = widthChangeCandidates.reduce(
					(
						best,
						candidate,
					) => {
						if (
							best === null
							|| Math.abs(candidate) < Math.abs(best)
						) {
							return candidate;
						}

						return best;
					},
					null as number | null,
				)!;

				if (deltaWidthChange !== 0) {
					/**
					 * 8. Apply the width adjustment and compute new height via aspect ratio
					 */
					const newWidth = objectModel.width + deltaWidthChange;
					const newHeight = objectModel.height + (deltaWidthChange * photoAspectRatio);

					/**
					 * 9. Adjust the object to keep the fixed corner anchored and round values
					 */
					const snappedObjectModel = adjustObjectForSnapping(
						newWidth,
						newHeight,
						objectModel,
						fixedCornerIndex,
						initialRotatedPoints,
					);

					return {
						...snappedObjectModel,
						width: Math.round(snappedObjectModel.width),
						height: Math.round(snappedObjectModel.height),
						x_axis: Math.round(snappedObjectModel.x_axis),
						y_axis: Math.round(snappedObjectModel.y_axis),
					};
				}
			}
		}

		/**
		 * No snap occurred (no band hit or invalid derivatives); return original model
		 */
		return objectModel;
	}

	/**
	 * Handle photo objects resized from side handles, scaling the crop rectangle
	 */
	if (
		isPhoto
		&& !isCornerHandle
	) {
		/**
		 * 1. Locate the midpoint of the dragged side in page coordinates
		 */
		const draggedMidpoint = getDraggedPoint(
			objectModel,
			resizeSide,
		);
		/**
		 * 2. Snap the midpoint along the relevant axis (X for left/right, Y for top/bottom)
		 */
		const snappedCoordinate = (
			(
				resizeSide === 'left'
				|| resizeSide === 'right'
			)
				? applySnapping(
					draggedMidpoint.x,
					pageMagneticLines.x.bands,
				)
				: applySnapping(
					draggedMidpoint.y,
					pageMagneticLines.y.bands,
				)
		);
		/**
		 * Calculate the shift needed to align the midpoint with the snapped position
		 */
		const shiftWanted = (
			(
				resizeSide === 'left'
				|| resizeSide === 'right'
			)
				? snappedCoordinate - draggedMidpoint.x
				: snappedCoordinate - draggedMidpoint.y
		);

		/**
		 * 3. Proceed only if snapping is needed (non-zero shift)
		 */
		if (shiftWanted !== 0) {
			/**
			 * 4. Perturb the relevant dimension to compute the derivative of the midpoint's position
			 */
			const objectPerturbed = adjustObjectForSnapping(
				(
					(
						resizeSide === 'left'
						|| resizeSide === 'right'
					)
						? objectModel.width + epsilonPerturb
						: objectModel.width
				),
				(
					(
						resizeSide === 'top'
						|| resizeSide === 'bottom'
					)
						? objectModel.height + epsilonPerturb
						: objectModel.height
				),
				objectModel,
				fixedCornerIndex,
				initialRotatedPoints,
			);
			const perturbedMidpoint = getDraggedPoint(
				objectPerturbed,
				resizeSide,
			);
			/**
			 * Compute derivative (∂x/∂w for left/right, ∂y/∂h for top/bottom)
			 */
			const derivative = (
				(
					resizeSide === 'left'
					|| resizeSide === 'right'
				)
					? (perturbedMidpoint.x - draggedMidpoint.x) / epsilonPerturb
					: (perturbedMidpoint.y - draggedMidpoint.y) / epsilonPerturb
			);

			/**
			 * 5. Check if the derivative is significant to avoid division by zero
			 */
			if (Math.abs(derivative) > epsilonZero) {
				/**
				 * 6. Calculate the raw dimension change needed to achieve the snap
				 */
				const deltaDimensionChange = shiftWanted / derivative;

				/**
				 * 7. Initialize trial dimensions based on the snap adjustment
				 */
				let trialWidth = objectModel.width;
				let trialHeight = objectModel.height;

				if (
					resizeSide === 'left'
					|| resizeSide === 'right'
				) {
					trialWidth += deltaDimensionChange;
				} else {
					trialHeight += deltaDimensionChange;
				}

				/**
				 * 8. Adjust crop rectangle and saturate against bitmap bounds
				 */
				let newCropX = objectModel.cropx;
				let newCropY = objectModel.cropy;
				let newCropWidth = objectModel.cropwidth;
				let newCropHeight = objectModel.cropheight;
				let adjustedWidth = trialWidth;
				let adjustedHeight = trialHeight;
				const cropBounds = getPageObjectCropBounds(objectModel);

				if (resizeSide === 'right') {
					/**
					 * Scale crop width proportionally to the new width
					 */
					newCropWidth = objectModel.cropwidth * (trialWidth / objectModel.width);
					const maxCropWidth = cropBounds.maxX - objectModel.cropx;

					/**
					 * Saturate crop width if it exceeds bitmap bounds
					 */
					if (newCropWidth > maxCropWidth) {
						newCropWidth = maxCropWidth;
						adjustedWidth = objectModel.width * (newCropWidth / objectModel.cropwidth);
					}
				} else if (resizeSide === 'left') {
					/**
					 * Scale crop width and adjust crop X to maintain bitmap alignment
					 */
					newCropWidth = objectModel.cropwidth * (trialWidth / objectModel.width);
					newCropX = objectModel.cropx + (objectModel.cropwidth - newCropWidth);

					/**
					 * Prevent crop X from passing the source lower bound.
					 */
					if (newCropX < cropBounds.minX) {
						newCropX = cropBounds.minX;
						newCropWidth = objectModel.cropx + objectModel.cropwidth - cropBounds.minX;
						adjustedWidth = objectModel.width * (newCropWidth / objectModel.cropwidth);
					}
				} else if (resizeSide === 'bottom') {
					/**
					 * Scale crop height proportionally to the new height
					 */
					newCropHeight = objectModel.cropheight * (trialHeight / objectModel.height);
					const maxCropHeight = cropBounds.maxY - objectModel.cropy;

					/**
					 * Saturate crop height if it exceeds bitmap bounds
					 */
					if (newCropHeight > maxCropHeight) {
						newCropHeight = maxCropHeight;
						adjustedHeight = objectModel.height * (newCropHeight / objectModel.cropheight);
					}
				} else if (resizeSide === 'top') {
					/**
					 * Scale crop height and adjust crop Y to maintain bitmap alignment
					 */
					newCropHeight = objectModel.cropheight * (trialHeight / objectModel.height);
					newCropY = objectModel.cropy + (objectModel.cropheight - newCropHeight);

					/**
					 * Prevent crop Y from passing the source lower bound.
					 */
					if (newCropY < cropBounds.minY) {
						newCropY = cropBounds.minY;
						newCropHeight = objectModel.cropy + objectModel.cropheight - cropBounds.minY;
						adjustedHeight = objectModel.height * (newCropHeight / objectModel.cropheight);
					}
				}

				/**
				 * 9. Re-anchor the fixed side and apply the adjusted dimensions
				 */
				const snappedObjectModel = adjustObjectForSnapping(
					(
						(
							resizeSide === 'left'
							|| resizeSide === 'right'
						)
							? adjustedWidth
							: objectModel.width
					),
					(
						(
							resizeSide === 'top'
							|| resizeSide === 'bottom'
						)
							? adjustedHeight
							: objectModel.height
					),
					objectModel,
					fixedCornerIndex,
					initialRotatedPoints,
				);

				/**
				 * 10. Return the snapped model with updated crop and rounded values
				 */
				return {
					...snappedObjectModel,
					cropx: Math.round(newCropX),
					cropy: Math.round(newCropY),
					cropwidth: Math.round(newCropWidth),
					cropheight: Math.round(newCropHeight),
					width: Math.round(snappedObjectModel.width),
					height: Math.round(snappedObjectModel.height),
					x_axis: Math.round(snappedObjectModel.x_axis),
					y_axis: Math.round(snappedObjectModel.y_axis),
				};
			}
		}

		/**
		 * No snap occurred (no band hit or invalid derivative); return original model
		 */
		return objectModel;
	}

	/**
	 * Handle text or free-aspect objects with independent width and height adjustments
	 */
	/**
	 * 1. Identify the dragged point (corner or side midpoint) in page coordinates
	 */
	const draggedPoint = getDraggedPoint(
		objectModel,
		resizeSide,
	);

	/**
	 * 2. Attempt to snap the dragged point's X and Y coordinates to magnetic bands
	 */
	const snappedXMid = applySnapping(
		draggedPoint.x,
		pageMagneticLines.x.bands,
	);
	const snappedYMid = applySnapping(
		draggedPoint.y,
		pageMagneticLines.y.bands,
	);
	/**
	 * Calculate the desired shifts to align with the snapped positions
	 */
	const snapDeltaXMid = snappedXMid - draggedPoint.x;
	const snapDeltaYMid = snappedYMid - draggedPoint.y;

	/**
	 * 3. Compute derivatives of the dragged point's position with respect to width and height
	 */
	const originalWidth = objectModel.width;
	const originalHeight = objectModel.height;

	/**
	 * Perturb width to estimate ∂x/∂w and ∂y/∂w
	 */
	const objectPlusWidth = adjustObjectForSnapping(
		originalWidth + epsilonPerturb,
		originalHeight,
		objectModel,
		fixedCornerIndex,
		initialRotatedPoints,
	);
	const pointPlusWidth = getDraggedPoint(
		objectPlusWidth,
		resizeSide,
	);
	const derivativePointXOverWidth = (pointPlusWidth.x - draggedPoint.x) / epsilonPerturb;
	const derivativePointYOverWidth = (pointPlusWidth.y - draggedPoint.y) / epsilonPerturb;

	/**
	 * Perturb height to estimate ∂x/∂h and ∂y/∂h
	 */
	const objectPlusHeight = adjustObjectForSnapping(
		originalWidth,
		originalHeight + epsilonPerturb,
		objectModel,
		fixedCornerIndex,
		initialRotatedPoints,
	);
	const pointPlusHeight = getDraggedPoint(
		objectPlusHeight,
		resizeSide,
	);
	const derivativePointXOverHeight = (pointPlusHeight.x - draggedPoint.x) / epsilonPerturb;
	const derivativePointYOverHeight = (pointPlusHeight.y - draggedPoint.y) / epsilonPerturb;

	/**
	 * 4. Solve for width and height adjustments to achieve the snap
	 */
	let deltaWidthAdjustment = 0;
	let deltaHeightAdjustment = 0;

	if (isCornerHandle) {
		/**
		 * For corners, solve a 2x2 system to adjust both width and height
		 */
		const determinant = (derivativePointXOverWidth * derivativePointYOverHeight) - (derivativePointXOverHeight * derivativePointYOverWidth);

		if (Math.abs(determinant) > epsilonZero) {
			/**
			 * Compute Δw and Δh using the inverse of the derivative matrix
			 */
			deltaWidthAdjustment = (derivativePointYOverHeight * snapDeltaXMid - derivativePointXOverHeight * snapDeltaYMid) / determinant;
			deltaHeightAdjustment = (-derivativePointYOverWidth * snapDeltaXMid + derivativePointXOverWidth * snapDeltaYMid) / determinant;
		}
	} else if (
		resizeSide === 'left'
		|| resizeSide === 'right'
	) {
		/**
		 * For left/right sides, adjust only width based on X snap
		 */
		if (Math.abs(derivativePointXOverWidth) > epsilonZero) {
			deltaWidthAdjustment = snapDeltaXMid / derivativePointXOverWidth;
		}
	} else if (
		resizeSide === 'top'
		|| resizeSide === 'bottom'
	) {
		/**
		 * For top/bottom sides, adjust only height based on Y snap
		 */
		if (Math.abs(derivativePointYOverHeight) > epsilonZero) {
			deltaHeightAdjustment = snapDeltaYMid / derivativePointYOverHeight;
		}
	}

	/**
	 * 5. Apply the calculated adjustments to width and height
	 */
	const finalWidth = objectModel.width + deltaWidthAdjustment;
	const finalHeight = objectModel.height + deltaHeightAdjustment;

	/**
	 * 6. Re-anchor the fixed corner/side and apply the new dimensions
	 */
	const snappedObjectModel = adjustObjectForSnapping(
		finalWidth,
		finalHeight,
		objectModel,
		fixedCornerIndex,
		initialRotatedPoints,
	);

	/**
	 * 7. Return the snapped model with rounded geometry values
	 */
	return {
		...snappedObjectModel,
		width: Math.round(snappedObjectModel.width),
		height: Math.round(snappedObjectModel.height),
		x_axis: Math.round(snappedObjectModel.x_axis),
		y_axis: Math.round(snappedObjectModel.y_axis),
	};
}

/**
 * Determine whether any anchor for a guide lies inside or within tolerance of a
 * magnetic band.
 * @param guide Guide definition being evaluated.
 * @param orientation Axis orientation the guide applies to.
 * @param band Magnetic band that may suppress the guide.
 * @param boundingBox Bounding box of the object in page space.
 * @param tolerance Allowed deviation in page units before the guide is considered outside.
 * @returns `true` when at least one anchor is inside the band tolerance.
 */
function valuesIntersectBand(
	guide: MagneticGuide,
	orientation: SnapGuideOrientation,
	band: MagneticBand,
	boundingBox: BoundingBox,
	tolerance: number,
): boolean {
	return getGuideTargetValues(
		guide.target,
		orientation,
		boundingBox,
	)
		.some((value) => (
			value >= band.min - tolerance
			&& value <= band.max + tolerance
		));
}

export async function addTextObject(options: AddTextObjectOptions): Promise<PageObjectModel | undefined> {
	const {
		objectModel,
		offeringModel,
		pageModel,
		pageObjects,
	} = options;
	const height = Math.round(Math.min(
		pageModel.width / 4,
		pageModel.height / 4,
	));
	const width = (
		!mobileTools.getIsMobile()
			? Math.round(pageModel.width / 2)
			: pageModel.width
	);
	const maxZAxis = (
		pageObjects?.reduce(
			(
				maxZ,
				pageObject,
			) => {
				if (pageObject.editable) {
					return Math.max(
						maxZ,
						pageObject.z_axis,
					);
				}

				return maxZ;
			},
			0,
		)
		?? 0
	);

	return (await updateTextObject({
		objectModel: {
			...JSON.parse(JSON.stringify(defaults.objectModel)),
			...objectModel,
			id: nanoid(),
			fontface: FontModule.getDefault.id,
			height,
			fontsize: Math.max(
				MIN_FONTSIZE,
				Math.round(pageModel.height / 20),
			),
			text: '',
			type: 'text',
			version: '12.30.0',
			width,
			x_axis: (
				!mobileTools.getIsMobile()
					? Math.round(pageModel.width / 2 - (width / 2))
					: 0
			),
			y_axis: Math.round(pageModel.height / 2 - (height / 2)),
			z_axis: maxZAxis + 1,
		},
		newData: {
			text: '',
		},
		offeringModel,
		pageModel,
	}))?.updatedPageObject;
}

/**
 * Applies print color settings from the product settings to the offering model if applicable.
 *
 * @param offeringModel {@link OfferingModel} to potentially update with print color settings.
 * @param productSettings Optional {@link ProductSettings} containing print color preferences and original offering identifiers.
 * @param productSettings.printColors Expected print color setting (e.g., 'fc' for full color or specific color codes).
 * @param productSettings.originalGroupId Original group ID to verify against the offering model.
 * @param productSettings.originalTypeId Original type ID to verify against the offering model.
 * @returns Updated {@link OfferingModel} with print color settings applied if conditions are met;
 * otherwise, returns the original offering model unchanged.
 */
export function applyPrintColorsToOfferingModel(
	offeringModel: OfferingModel,
	productSettings?: ProductSettings,
): OfferingModel {
	const {
		originalGroupId,
		originalTypeId,
		printColors,
	} = (
		productSettings
		?? {}
	);

	if (
		typeof printColors === 'undefined'
		|| typeof originalGroupId === 'undefined'
		|| typeof originalTypeId === 'undefined'
		|| originalGroupId !== offeringModel.groupid
		|| originalTypeId !== offeringModel.typeid
	) {
		return offeringModel;
	}

	if (printColors === 'fc') {
		const fullColorSpectrum = (
			[
				offeringModel.previewColorSpectrum,
				offeringModel.printColorSpectrum,
			]
				.find((spectrum): spectrum is string => (
					typeof spectrum === 'string'
					&& PRINT_COLOR_SPECTRUM_FULL.includes(spectrum)
				))
			|| 'srgb'
		);

		return {
			...offeringModel,
			color: COLOR_FULL,
			previewColorSpectrum: fullColorSpectrum,
			printColorSpectrum: fullColorSpectrum,
		};
	}

	if (offeringModel.color <= printColors) {
		return offeringModel;
	}

	return {
		...offeringModel,
		color: printColors,
		previewColorSpectrum: `pms_${printColors}`,
		printColorSpectrum: `pms_${printColors}`,
	};
}

/**
 * Applies an SKU quantity from a route query to the product settings and removes the URL parameter.
 *
 * @param query - Query dictionary from the current route (see {@link LocationQuery}).
 * @param options - Optional overrides controlling whether product settings or the URL are mutated.
 * - `removeUrlParameter` (default: `true`) – Whether to remove the `skuQuantity` parameter from the URL.
 * - `updateProductSettings` (default: `true`) – Whether to update the product settings with the extracted SKU quantity.
 * @returns Parsed SKU quantity when present and valid; otherwise `undefined`.
 */
export async function applySkuQuantityFromQuery(
	query?: LocationQuery,
	options?: {
		removeUrlParameter?: boolean;
		updateProductSettings?: boolean;
	},
): Promise<number | undefined> {
	const skuQuantity = getSkuQuantityFromQuery(query);

	if (typeof skuQuantity === 'undefined') {
		return undefined;
	}

	const {
		updateProductSettings = true,
		removeUrlParameter = true,
	} = (
		options
		?? {}
	);

	if (updateProductSettings) {
		ProductStateModule.changeProductSettings({
			skuQuantity,
		});
	}

	if (
		removeUrlParameter
		&& window?.App?.router?.removeURLParameter
	) {
		try {
			await window.App.router.removeURLParameter(URL_PARAMETERS.skuQuantity);
		} catch {
			/**
			 * Shallow error: no action required, URL parameter cleanup is best effort
			 */
		}
	}

	return skuQuantity;
}

/**
 * Calculates the axis-aligned {@link BoundingBoxRect} that encloses a rotated
 * {@link PageObjectModel}. The computation derives the object's rotated corner
 * positions and then determines the minimum and maximum coordinates across both
 * axes to build the bounding rectangle.
 *
 * @param objectModel {@link PageObjectModel} containing the `x_axis`, `y_axis`,
 * `width`, `height`, and `rotate` data required to derive the rotated corners.
 * @returns {@link BoundingBoxRect} describing the enclosing rectangle in page
 * coordinates.
 */
export function calculateRotatedBoundingBox(objectModel: OptionalExceptFor<PageObjectModel, 'height' | 'rotate' | 'width' | 'x_axis' | 'y_axis' >): BoundingBoxRect {
	const boundingBox = getBoundingBox(getRotatedCorners(objectModel));

	return {
		height: boundingBox.maxY - boundingBox.minY,
		width: boundingBox.maxX - boundingBox.minX,
		x: boundingBox.minX,
		y: boundingBox.minY,
	};
}

/**
 * Checks for changes in the offering's spine configuration and updates the
 * product state accordingly.
 * This function compares the current spine model in the product state with
 * the spine configuration defined in the offering model.
 * - If there is a spine available for the offering model but not in the
 * project data, it adds the spine page to the project data.
 * - If there is no spine available for the offering model but there is a spine
 * in the project data, it removes the spine page from the project data.
 * - If there is a spine in both the offering model and the project data but their
 * widths differ, it updates the spine page in the project data to match the width
 * defined in the offering model. It also adjusts the position of any objects on
 * the spine page to maintain their relative placement.
 *
 * @param offeringModel {@link OfferingModel} containing the current offering configuration,
 * including the spine configuration.
 */
export async function checkUpdateSpine(offeringModel: OfferingModel): Promise<void> {
	if (experiment.isFeatureEnabled('flag_book_spine')) {
		const existingSpineModel = ProductStateModule.getSpine;

		if (
			offeringModel.spine?.length
			&& !existingSpineModel
		) {
			return ProductState
				.addSpine()
				.then(() => undefined);
		}

		if (
			!offeringModel.spine?.length
			&& existingSpineModel
		) {
			return ProductStateModule
				.removeSpine()
				.then(() => undefined);
		}

		if (!offeringModel.spine?.length) {
			return undefined;
		}

		const spineModelToUse = getSpineModel(
			offeringModel,
			ProductStateModule.getPages.length,
		);

		if (
			existingSpineModel
			&& spineModelToUse.width !== existingSpineModel.width
		) {
			ProductStateModule.changePage({
				id: existingSpineModel.id,
				width: spineModelToUse.width,
			});
			const spineObjects = ProductStateModule.getPageObjects(existingSpineModel);
			const templatePosition = ProductStateModule.getPageTemplatePositions({
				pageModel: ProductStateModule.getSpineForViewing!,
				dpi: offeringModel.configdpi,
			})[0] as TemplateTextPosition;

			for (const spineObject of spineObjects) {
				let fontSize = (
					spineObject.fontsize
					?? pointToPixel(spineObject.pointsize!)
				);
				const fitResult = fit({
					fontFamily: getFontFamily({
						family: spineObject.fontface!,
						bold: !!spineObject.fontbold,
						italic: !!spineObject.fontitalic,
					}),
					fontModel: FontModule.getById(spineObject.fontface!),
					fontSize,
					isBold: !!spineObject.fontbold,
					maxWidth: templatePosition.width,
					text: spineObject.text || '',
					kind: 'font',
					boxHeight: templatePosition.height,
					boxWidth: templatePosition.width,
					minFontSize: MIN_FONTSIZE,
					maxFontSize: existingSpineModel.height,
				});

				/**
				 * We only want to reduce the font size if it exceeds the new spine height,
				 * basically out of bounds, but we don't want to increase it if it's smaller
				 * than the new spine height, to avoid unintended changes to the user's chosen
				 * font size when the spine width is updated to be larger.
				 */
				if (fitResult.usedFontSize < fontSize) {
					fontSize = fitResult.usedFontSize;
				}

				await ProductStateModule.changePageObject({
					id: spineObject.id,
					height: templatePosition.height,
					x_axis: templatePosition.x,
					y_axis: templatePosition.y,
					fontsize: fontSize,
				});
			}
		}
	}

	return undefined;
}

/**
 * Scales a horizontal coordinate using the current page dimensions.
 *
 * @param value Horizontal coordinate to scale.
 * @returns Scaled coordinate.
 */
export function createScaleHorizontalCoordinateFn(
	sourceWidth: number,
	sourceOffset: number,
	target: OptionalExceptFor<ScaleTarget, 'width'>,
	targetOffset: number,
): (value: number) => number {
	return (value) => scaleAxisCoordinate(
		value,
		sourceWidth,
		sourceOffset,
		target.width,
		targetOffset,
	);
}

/**
 * Scales a vertical coordinate using the current page dimensions.
 *
 * @param value Vertical coordinate to scale.
 * @returns Scaled coordinate.
 */
export function createScaleVerticalCoordinateFn(
	sourceHeight: number,
	sourceOffset: number,
	target: OptionalExceptFor<ScaleTarget, 'height'>,
	targetOffset: number,
): (value: number) => number {
	return (value) => scaleAxisCoordinate(
		value,
		sourceHeight,
		sourceOffset,
		target.height,
		targetOffset,
	);
}

/**
 * Constructs a sanitized SVG string that represents a text {@link PageObjectModel}
 * using its typography, alignment, background, and positional attributes. The SVG
 * disables contextual OpenType features to keep glyph shaping consistent and
 * strips HTML entities so downstream PDF generation receives clean markup.
 *
 * @param objectModel {@link PageObjectModel} containing the dimensions, font
 * properties, optional background color, positional attributes, and text content
 * used to build the SVG.
 * @returns Stringified SVG markup reflecting the provided {@link PageObjectModel}
 * settings with HTML entities removed.
 */
export function createTextSVGString(objectModel: PageObjectModel): string {
	const fontSize = (
		objectModel.fontsize
		?? pointToPixel(objectModel.pointsize!)
	);
	const fontFamily = getFontFamily({
		family: objectModel.fontface!,
		bold: !!objectModel.fontbold,
		italic: !!objectModel.fontitalic,
	});
	const lineHeight = getEffectiveLineHeight({
		fontFamily,
		fontModel: FontModule.getById(objectModel.fontface!),
		fontSize,
		isBold: !!objectModel.fontbold,
	});
	const svgElement = document.createElementNS(
		'http://www.w3.org/2000/svg',
		'svg',
	);
	svgElement.setAttribute(
		'height',
		String(objectModel.height),
	);
	svgElement.setAttribute(
		'width',
		String(objectModel.width),
	);
	svgElement.setAttribute(
		'viewBox',
		`0 0 ${objectModel.width} ${objectModel.height}`,
	);
	/**
	 * OpenType feature "issue":
	 * Some fonts uses contextual alternates (the `calt` feature-and sometimes `rand`)
	 * so glyph shapes can change based on run boundaries and position.
	 * Since the SVG text renders each line in a separate `<tspan>`, each line is treated
	 * as a new shaping run, causing some letters to change shape when compared to the
	 * same text rendered in a single line in a `<textarea>` element.
	 * To avoid this, we disable the `calt` and `rand` features for SVG text elements here.
	 * - Note that this needs to be also disabled where the `text` is rendered in a `textarea`
	 * element.
	 */
	svgElement.setAttribute(
		'style',
		'font-variant-ligatures: no-contextual; font-feature-settings: \'calt\' 0, \'rand\' 0;',
	);

	if (objectModel.positionAttributes) {
		const { positionAttributes } = objectModel;
		svgElement.setAttribute(
			'style',
			Object
				.keys(positionAttributes)
				.reduce(
					(
						styles,
						attributeKey,
					) => (
						`${styles} ${attributeKey}: ${positionAttributes[attributeKey]};`
					),
					svgElement.getAttribute('style')!,
				),
		);
	}

	if (objectModel.bgcolor) {
		svgElement.appendChild(
			domElementToHTMLElement(
				createDomElement(
					'rect',
					{
						x: 0,
						y: 0,
						fill: objectModel.bgcolor,
						height: objectModel.height,
						width: objectModel.width,
					},
					undefined,
					'http://www.w3.org/2000/svg',
				),
			),
		);
	}

	svgElement.appendChild(
		domElementToHTMLElement(
			textareaToDomElementText({
				fill: objectModel.fontcolor!,
				fontFamily,
				fontSize,
				lineHeight,
				maxWidth: objectModel.width,
				text: insertSoftHyphens({
					fontFamily,
					fontSize,
					maxWidth: objectModel.width,
					text: objectModel.text!,
				}),
				textAlign: objectModel.align!.toLowerCase() as Lowercase<NonNullable<PageObjectModel['align']>>,
			}),
		),
	);
	/**
	 * Strip html entities from text svg nodes - we can't use htmlentities in the PDF generator
	 */
	const svgString = stripHtmlEntities(new XMLSerializer().serializeToString(svgElement));
	svgElement.remove();

	return svgString;
}

/**
 * Resolves the attributes for a {@link TemplatePositionTextModel}, parsing JSON
 * strings when present and normalizing legacy flat style maps to the structured
 * attributes shape. When parsing fails, the error is reported to Bugsnag as a
 * warning so malformed data can be observed without interrupting user flows.
 *
 * @param positionModel {@link TemplatePositionTextModel} containing attributes
 * either as a JSON string or typed object.
 * @returns The normalized {@link TemplateTextPosition} attributes, or `null`
 * when no attributes are available or parsing fails.
 */
export function getAndParseTemplatePositionTextModelAttributes(positionModel: TemplatePositionTextModel): TemplatePositionTextAttributesModel | null {
	let attributes: TemplatePositionTextAttributesModel = {};

	if (typeof positionModel.attributes === 'string') {
		try {
			attributes = normalizeTemplatePositionTextAttributes(JSON.parse(positionModel.attributes));
		} catch {
			reportError(
				new Error(`Could not parse TemplatePositionTextModel attributes JSON string for TemplatePositionStateModel ID ${positionModel.id}`),
				'warning',
			);
		}
	} else if (
		typeof positionModel.attributes !== 'undefined'
		&& positionModel.attributes !== null
	) {
		attributes = normalizeTemplatePositionTextAttributes(positionModel.attributes);
	}

	if (hasTemplatePositionTextAttributes(attributes)) {
		return attributes;
	}

	return null;
}

/**
 * Resolves how many backend-only fixed pages should be counted.
 *
 * @param offeringModel Offering currently selected for the project.
 * @param fixedPageStateModels Theme-level backend-only fixed page models.
 * @returns Fixed-page count for eligible offerings; otherwise zero.
 */
export function getBackendOnlyFixedPagesCount(
	offeringModel: OfferingModel | null | undefined,
	fixedPageStateModels: BackendOnlyFixedPageStateModel[],
): number {
	if (!isBookTypeOffering(offeringModel)) {
		return 0;
	}

	return fixedPageStateModels.length;
}

export function getCropFromRatio(options: CropFromRatioOptions): number {
	const { object } = options;

	if (object.maxheight < object.maxwidth) {
		const cropRatio = object.cropwidth / object.cropheight;

		if (options.type === 'height') {
			return options.value / cropRatio;
		}

		return options.value * cropRatio;
	}

	const cropRatio = object.cropheight / object.cropwidth;

	if (options.type === 'height') {
		return options.value * cropRatio;
	}

	return options.value / cropRatio;
}

export function getFullOfferingModelColorQuantity(
	fullOfferingModel?: FullOfferingModel | null,
): number {
	const colorModel = fullOfferingModel?.options?.find((option) => option.tag === 'color-quantity')?.value;

	if (!colorModel) {
		return COLOR_FULL;
	}

	/**
	 * If the value is directly a string, return default
	 */
	if (typeof colorModel.value === 'string') {
		return COLOR_FULL;
	}

	/**
	 * If value is a number, return it
	 */
	if (typeof colorModel.value === 'number') {
		return colorModel.value;
	}

	/**
	 * If there's an id property and it's defined, parse and return it
	 */
	if (
		'id' in colorModel
		&& colorModel.id !== undefined
	) {
		return parseInt(
			String(colorModel.id),
			10,
		);
	}

	return COLOR_FULL;
}

/**
 * Computes the minimum allowed {@link BoundingBox} for a {@link PageObjectModel}
 * so it remains at least `minVisiblePixels` visible within the containing
 * {@link PageModel}. The calculation caps the minimum visibility to the object's
 * own dimensions to avoid negative offsets on small objects.
 *
 * @param objectModel {@link PageObjectModel} supplying the width and height used
 * to derive visibility thresholds.
 * @param pageModel {@link PageModel} providing the page width and height limits.
 * @param minVisiblePixels Minimum number of pixels that must stay visible on each
 * edge before the object is considered out of bounds. Defaults to
 * {@link MINIMUM_VISIBLE_PIXELS}.
 * @returns {@link BoundingBox} describing the min/max x/y coordinates that keep
 * the object within the page while respecting visibility requirements.
 */
export function getMinimalPageObjectBoundingBox(
	objectModel: PageObjectModel,
	pageModel: PageModel,
	minVisiblePixels = MINIMUM_VISIBLE_PIXELS,
): BoundingBox {
	const minVisibleWidth = Math.min(
		minVisiblePixels,
		objectModel.width,
	);
	const minVisibleHeight = Math.min(
		minVisiblePixels,
		objectModel.height,
	);

	return {
		minX: minVisibleWidth - objectModel.width,
		maxX: pageModel.width - minVisibleWidth,
		minY: minVisibleHeight - objectModel.height,
		maxY: pageModel.height - minVisibleHeight,
	};
}

/**
 * Get a display-safe URL for browser-rendered image elements.
 *
 * The `noCorsHeader` parameter keeps these requests separate from canvas image
 * loads, so a non-CORS display request cannot poison the browser cache for
 * later canvas rendering.
 */
export function getNoCORSUrl(imageUrl?: string): string {
	if (
		!imageUrl
		|| typeof imageUrl !== 'string'
	) {
		return '';
	}

	if (
		imageUrl.startsWith('blob:')
		|| imageUrl.startsWith('data:')
	) {
		return imageUrl;
	}

	if (isPresignedUrl(imageUrl)) {
		return imageUrl;
	}

	return appendUrlParameter(
		imageUrl,
		'noCorsHeader',
	);
}

export function getOfferingModelColorQuantity(offeringModel: OptionalExceptFor<OfferingModel, 'previewColorSpectrum'>): number {
	if (
		!offeringModel.previewColorSpectrum
		|| PRINT_COLOR_SPECTRUM_FULL.includes(offeringModel.previewColorSpectrum)
	) {
		return COLOR_FULL;
	}

	if (offeringModel.previewColorSpectrum.startsWith('pms_')) {
		return parseInt(
			offeringModel.previewColorSpectrum.replace(
				'pms_',
				'',
			),
			10,
		);
	}

	return 1;
}

/**
 * Determines whether the offering is a logo product printed in full color.
 *
 * @param offeringModel {@link OfferingModel} context used to evaluate the print color flow.
 * @returns True when the offering is a full-color logo product.
 */
export function getOfferingModelIsFullColorLogoProduct(offeringModel?: OptionalExceptFor<OfferingModel, 'previewColorSpectrum' | 'type'> | null): boolean {
	return !!(
		offeringModel?.type === 'logo'
		&& getOfferingModelColorQuantity(offeringModel) === COLOR_FULL
	);
}

export function getOfferingModelIsFullColorCapable(offeringModel?: OfferingModel | null): boolean {
	/**
	 * Note: Upon migration to JSON schema this check can be performed directly on the property
	 * in the offering data, but for now we need to compile the boolean value like this
	 */
	if (
		offeringModel?.printColorSpectrum
		&& PRINT_COLOR_SPECTRUM_FULL.includes(offeringModel?.printColorSpectrum)
	) {
		return true;
	}

	return false;
}

export function getOfferingModelPrintEffectColor(offeringModel: OptionalExceptFor<OfferingModel, 'previewColorSpectrum'>): string | undefined {
	if (!offeringModel.previewColorSpectrum) {
		return undefined;
	}

	if (
		PRINT_COLOR_SPECTRUM_FULL.includes(offeringModel.previewColorSpectrum)
		|| offeringModel.previewColorSpectrum.startsWith('pms_')
	) {
		return undefined;
	}

	try {
		return colorUtils.getHexFromAny(offeringModel.previewColorSpectrum);
	} catch {
		// Shallow error: no action required
	}

	return undefined;
}

/**
 * Retrieves the display label for a given offering option value, applying special
 * handling for print color options and utilizing translations when available.
 *
 * @param options {@link GetOfferingOptionValueLabelOptions} containing the necessary context to resolve
 * @param options.offeringModel The current {@link OfferingModel} which may contain relevant data for resolving the label.
 * @param options.offeringOption The specific {@link OfferingOptionModel} for which the value label is being retrieved,
 * used to apply special handling for certain option tags like 'print-color'.
 * @param options.productOptionValue The {@link ProductOptionValueModel} representing the selected value for the offering
 * option, which may contain an ID used for translation lookup and a raw value.
 * @param options.fullOfferingModels An array of {@link FullOfferingModel} instances that may be searched to find related
 * offering options, particularly for resolving full color labels.
 * @param options.productSettings The current {@link ProductSettings} which may contain print color preferences that
 * influence how certain option values are resolved and displayed.
 * @returns The resolved label for the offering option value, taking into account translations and special handling for
 * print color options.
 */
export function getOfferingOptionValueLabel(options: GetOfferingOptionValueLabelOptions): string {
	const {
		offeringModel,
		offeringOption,
		productOptionValue,
		fullOfferingModels,
		productSettings,
	} = options;
	// Set resolved translation value
	let resolvedTranslation: string | undefined;
	const printMethodTranslationKeys: Record<string, string> = {
		'Bulb printing': 'printMethods.bulbPrinting',
		'Ceramic printing': 'printMethods.ceramicPrinting',
		'Ceramic transfer': 'printMethods.ceramicTransfer',
		Debossing: 'printMethods.debossing',
		'Digital printing': 'printMethods.digitalPrinting',
		'Digital transfer': 'printMethods.digitalTransfer',
		Doming: 'printMethods.doming',
		'DTF transfer': 'printMethods.dtfTransfer',
		'DTG printing': 'printMethods.dtgPrinting',
		Emblem: 'printMethods.emblem',
		Embossing: 'printMethods.embossing',
		Engraving: 'printMethods.engraving',
		Etching: 'printMethods.etching',
		'Flower printing': 'printMethods.flowerPrinting',
		'Foil printing': 'printMethods.foilPrinting',
		Hotstamp: 'printMethods.hotstamp',
		IML: 'printMethods.iml',
		'Laser engraving': 'printMethods.laserEngraving',
		'Offset printing': 'printMethods.offsetPrinting',
		'Pad printing': 'printMethods.padPrinting',
		'Plant printing': 'printMethods.plantPrinting',
		'Puff printing': 'printMethods.puffPrinting',
		'Relief moulding': 'printMethods.reliefMoulding',
		'Screen printing': 'printMethods.screenPrinting',
		'Screen transfer': 'printMethods.screenTransfer',
		'Silk screen printing': 'printMethods.silkScreenPrinting',
		'Sticker printing': 'printMethods.stickerPrinting',
		Sublimation: 'printMethods.sublimation',
		Transfer: 'printMethods.transfer',
		'UV printing': 'printMethods.uvPrinting',
		'UV DTF printing': 'printMethods.uvDTFPrinting',
		'Vinyl printing': 'printMethods.vinylPrinting',
		'Woven emblem': 'printMethods.wovenEmblem',
	};
	const translationKey = printMethodTranslationKeys[productOptionValue.value];

	if (translationKey) {
		resolvedTranslation = window.App.router.$t(translationKey);
	}

	if (productOptionValue.id) {
		resolvedTranslation = window.App.router.$t(
			`offeringOptionValues:${productOptionValue.id}`,
			(
				resolvedTranslation
				|| String(productOptionValue.value)
			),
		);
	}

	if (
		resolvedTranslation
		&& offeringOption.tag === 'print-color'
	) {
		const { value: resolvedValue } = resolveOfferingOptionValue(
			productOptionValue as unknown as OfferingOptionValueModel,
			offeringOption,
			{
				offeringid: (
					productOptionValue.offeringid
					?? offeringModel.id
				),
			},
			productSettings,
		);

		if (
			String(resolvedValue) !== resolvedTranslation
			&& resolvedValue !== COLOR_FULL
		) {
			return String(resolvedValue);
		}
		if (
			resolvedValue === COLOR_FULL
			&& productOptionValue.value !== 'full-color'
		) {
			const existingFullColorOfferingOptionValue = fullOfferingModels
				.find((fullOfferingModel) => (
					fullOfferingModel.options.some((option) => (
						option.tag === 'print-color'
						&& option.value.value === 'full-color'
					))
				))
				?.options.find((option) => (
					option.tag === 'print-color'
					&& option.value.value === 'full-color'
				))?.value;

			return (
				existingFullColorOfferingOptionValue
					? window.App.router.$t(
						`offeringOptionValues:${existingFullColorOfferingOptionValue.id}`,
						String(existingFullColorOfferingOptionValue.value),
					)
					: 'full-color'
			);
		}

		return resolvedTranslation;
	}

	if (resolvedTranslation) {
		return resolvedTranslation;
	}

	if (productOptionValue.offeringid) {
		return (
			fullOfferingModels.find((fullOfferingModel) => (
				fullOfferingModel.id === productOptionValue.offeringid
			))?.size
			|| String(productOptionValue.value)
		);
	}

	return String(productOptionValue.value);
}

export function getOverviewPageLabel(
	initialValue: FullPageModel['label'],
	fullPageModel: OptionalExceptFor<FullPageModel, 'index' | 'model'>,
	offeringModel?: OfferingModel | null,
	productModel?: ProductModel | null,
): FullPageModel['label'] {
	if (
		productModel
		&& OfferingGroups(
			productModel.group,
			'CardGame',
		)
	) {
		if (fullPageModel.index === 1) {
			return window.App.router.$t('coverInside');
		}
		if (fullPageModel.index > 1) {
			return '';
		}
	}

	if (offeringModel) {
		if (
			OfferingGroups(
				offeringModel.groupid,
				'PhotoFrameBox',
			)
		) {
			if (fullPageModel.index === 0) {
				return window.App.router.$t('topCardFront');
			}
			if (fullPageModel.index === 1) {
				return window.App.router.$t('topCardBack');
			}
		}
		if (
			OfferingGroups(
				offeringModel.groupid,
				'PhotoSheets',
			)
		) {
			if (fullPageModel.index % 2) {
				return window.App.router.$t('pageBackSide');
			}

			return window.App.router.$t('pageFrontSide');
		}
		if (
			OfferingGroups(
				offeringModel.groupid,
				'PrintTypes',
			)
		) {
			if (
				offeringModel.virtual
				&& fullPageModel.model.offeringId
			) {
				const activeOfferingModel = AppDataModule.getOffering(fullPageModel.model.offeringId);

				if (activeOfferingModel) {
					let label = activeOfferingModel.size;
					const variantName = AppDataModule.getOfferingVariantName(fullPageModel.model.offeringId);

					if (variantName) {
						label += `, ${variantName}`;
					}

					return label;
				}
			}

			return offeringModel.size;
		}
	}

	return initialValue;
}

/**
 * Resolves the page count used by min/max page validation logic.
 *
 * @param designablePageCount Number of designable pages currently in the project.
 * @param backendOnlyFixedPagesCount Number of backend-only fixed pages.
 * @returns Total page count used for limit checks.
 */
export function getPageCountForLimitChecks(
	designablePageCount: number,
	backendOnlyFixedPagesCount: number,
): number {
	return designablePageCount + backendOnlyFixedPagesCount;
}

/**
 * Calculates how many blank pages are needed to reach the next valid production page count.
 *
 * @param currentPageCount Current page count used by product limit checks.
 * @param offeringModel Offering that defines production page requirements.
 * @returns Number of pages to add, or zero when the current count is valid.
 */
export function getPageCountCompensationQuantity(
	currentPageCount: number,
	offeringModel: Pick<OfferingModel, 'minpages' | 'minprintpages' | 'pageinterval'>,
): number {
	const pageInterval = Math.max(
		1,
		offeringModel.pageinterval,
	);

	if (pageInterval <= 1) {
		return 0;
	}

	let targetPageCount = Math.max(
		currentPageCount,
		offeringModel.minpages,
		offeringModel.minprintpages,
	);
	const intervalRest = (targetPageCount - offeringModel.minprintpages + pageInterval) % pageInterval;

	if (intervalRest > 0) {
		targetPageCount += pageInterval - intervalRest;
	}

	return Math.max(
		0,
		targetPageCount - currentPageCount,
	);
}

/**
 * Resolves the total quantity of printed pages.
 *
 * @param designablePagesQuantity Sum of page quantities in the editable project data.
 * @param backendOnlyFixedPagesCount Number of backend-only fixed pages.
 * @returns Printed page quantity including backend-only fixed pages.
 */
export function getPrintedPagesQuantity(
	designablePagesQuantity: number,
	backendOnlyFixedPagesCount: number,
): number {
	return designablePagesQuantity + backendOnlyFixedPagesCount;
}

/**
 * Extracts a numeric SKU quantity value from a route query object.
 *
 * @param query - Query dictionary from the current route (see {@link LocationQuery}).
 * @returns Parsed SKU quantity when present and valid; otherwise `undefined`.
 */
export function getSkuQuantityFromQuery(query?: LocationQuery): number | undefined {
	if (!query) {
		return undefined;
	}

	const rawSkuQuantity = query[URL_PARAMETERS.skuQuantity];

	if (typeof rawSkuQuantity !== 'string') {
		return undefined;
	}

	if (!/^\d+$/.test(rawSkuQuantity)) {
		return undefined;
	}

	const skuQuantity = parseInt(
		rawSkuQuantity,
		10,
	);

	if (skuQuantity > 0) {
		return skuQuantity;
	}

	return undefined;
}

export function getSpineModel(
	offeringModel: OfferingModel,
	pageCount: number,
): SpineModel {
	if (!offeringModel.spine?.length) {
		throw new Error('Offering model does not include spine data');
	}

	const spineModelToUse = (
		offeringModel.spine
			.filter((spineModel) => pageCount >= spineModel.threshold)
			.sort((a, b) => a.threshold - b.threshold)
			.at(-1)
	);

	if (!spineModelToUse) {
		throw new Error(`No spine model matches the page count threshold of ${pageCount} for offering ${offeringModel.id}`);
	}

	const spineWidthInPixels = (
		ConfigModule.metricUnits === 'in'
			? inToPx(
				spineModelToUse.width,
				offeringModel.configdpi,
				true,
			)
			: mmToPx(
				spineModelToUse.width,
				offeringModel.configdpi,
				true,
			)
	);

	return {
		...spineModelToUse,
		width: Math.max(
			80,
			spineWidthInPixels,
		),
	};
}

/**
 * Determines whether the provided page model contains at least one editable object.
 *
 * @param pageModel Single {@link FullPageModel} to inspect for editable objects.
 * @returns True when the page model includes an editable object; otherwise false.
 */
export function hasEditableObjects(pageModel: FullPageModel): boolean;
/**
 * Determines whether the provided page model collection contains at least one editable object.
 *
 * @param pageModels Collection of {@link FullPageModels} to inspect for editable objects.
 * @returns True when any page model includes an editable object; otherwise false.
 */
export function hasEditableObjects(pageModels: FullPageModels): boolean;
/**
 * Determines whether the provided page model or collection of page models contains at least one editable object.
 *
 * @param pageModelOrModels {@link FullPageModel} or collection of {@link FullPageModels} to inspect for editable objects.
 * @returns True when any page model includes an editable object; otherwise false.
 */
export function hasEditableObjects(pageModelOrModels: FullPageModel | FullPageModels): boolean {
	return (
		Array.isArray(pageModelOrModels)
			? pageModelOrModels
			: [pageModelOrModels]
	)
		.filter((fullPageModel) => fullPageModel.objects.length)
		.some((fullPageModel) => fullPageModel.objects.some((object) => object.editable));
}

/**
 * Injects backend-only fixed pages into the array used for viewing.
 *
 * @param options Input data needed to derive the viewing pages.
 * @returns Nothing.
 */
export function insertBackendOnlyFixedPagesForViewing(
	options: InsertBackendOnlyFixedPagesForViewingOptions,
): void {
	const {
		fixedPageStateModels,
		offeringModel,
		pageModels,
	} = options;

	if (
		!isBookTypeOffering(offeringModel)
		|| !fixedPageStateModels.length
	) {
		return;
	}

	const trailingInsideCoverPagesCount = (
		offeringModel.startright
			? 1
			: 0
	);

	fixedPageStateModels.forEach((fixedPageStateModel) => {
		const insertionBaseIndex = pageModels.length - trailingInsideCoverPagesCount;
		const insertionIndex = getFixedPageInsertionIndex(
			insertionBaseIndex,
			fixedPageStateModel.serialnumber,
		);

		pageModels.splice(
			insertionIndex,
			0,
			{
				...fixedPageStateModel.pageModel,
				id: `${BACKEND_ONLY_FIXED_PAGE_ID_PREFIX}${fixedPageStateModel.id}`,
				editable: 0,
				movable: 0,
				quantity: 1,
				serialnumber: fixedPageStateModel.serialnumber,
			},
		);
	});
}

/**
 * Converts inches to pixels based on DPI
 *
 * @param inches The value in inches
 * @param dpi The DPI value
 * @param round Whether to round the result to the nearest integer (default: false)
 * @returns The value in pixels
 */
export function inToPx(
	inches: number,
	dpi: number,
	round?: boolean,
): number {
	const px = inches * dpi;

	return (
		round
			? Math.round(px)
			: px
	);
}

/**
 * Getter to check if this is a basic product that has multiple sides/positions to be edited
 * (for instance, front and back of a t-shirt)
 *
 * @returns {boolean} Returns true if the product is multi sided
 */
export function isMultiSidedProduct(offeringModel?: OfferingModel | null): boolean {
	if (
		offeringModel
		&& OfferingGroups(
			offeringModel.groupid,
			['BasicProducts'],
		)
	) {
		return offeringModel.minpages > 1;
	}

	return false;
}

export function isPageCoverBack(
	offeringModel: OptionalExceptFor<OfferingModel, 'groupid'>,
	pageId: PageModel['id'],
	pageIndex: number,
): boolean {
	if (
		OfferingGroups(
			offeringModel.groupid,
			'BookTypes',
		)
		&& (
			pageId === COVER_BACK
			|| pageIndex === 0
		)
	) {
		return true;
	}

	return false;
}

export function isPageCoverFront(
	offeringModel: OptionalExceptFor<OfferingModel, 'groupid'>,
	pageId: PageModel['id'],
	pageIndex: number,
): boolean {
	if (
		OfferingGroups(
			offeringModel.groupid,
			'CardGame',
		)
		&& pageIndex === 0
	) {
		return true;
	}

	if (
		OfferingGroups(
			offeringModel.groupid,
			'BookTypes',
		)
		&& (
			pageId === COVER_FRONT
			|| pageIndex === 1
		)
	) {
		return true;
	}

	return false;
}

export function isPageCoverInside(
	offeringModel: OptionalExceptFor<OfferingModel, 'groupid'>,
	pageId: PageModel['id'],
): boolean {
	if (
		OfferingGroups(
			offeringModel.groupid,
			'BookTypes',
		)
		&& (
			pageId === COVER_BACK_INSIDE
			|| pageId === COVER_FRONT_INSIDE
		)
	) {
		return true;
	}

	return false;
}

export function isPageSpine(
	offeringModel: OptionalExceptFor<OfferingModel, 'groupid'>,
	pageId: PageModel['id'],
): boolean {
	if (
		OfferingGroups(
			offeringModel.groupid,
			'BookTypes',
		)
		&& (
			pageId === SPINE
			|| pageId === ProductStateModule.getSpine?.id
		)
	) {
		return true;
	}

	return false;
}

/**
 * Determines whether the provided {@link PageObjectModel} is a sticker.
 *
 * @param objectModel The {@link PageObjectModel} to check.
 * @returns True when the object references a sticker asset and lacks a linked photo id.
 */
export function isStickerPageObject(objectModel: PageObjectModel): boolean {
	if (objectModel.type !== 'photo') {
		return false;
	}

	/**
	 * Non-empty sources without `photoid` represent sticker assets stored
	 * in the theme data catalog.
	 * In the future we could add a new `type` to be `sticker`.
	 */
	if (
		typeof objectModel.source === 'string'
		&& objectModel.source.trim().length > 0
		&& (
			typeof objectModel.photoid === 'undefined'
			|| objectModel.photoid === null
		)
	) {
		return true;
	}

	return false;
}

/**
 * Converts millimeters to pixels based on DPI
 * @param mm The value in millimeters
 * @param dpi The DPI value
 * @param round Whether to round the result to the nearest integer (default: false)
 * @returns The value in pixels
 */
export function mmToPx(
	mm: number,
	dpi: number,
	round?: boolean,
): number {
	const mmPerInch = 25.4;
	const px = (mm * dpi) / mmPerInch;

	return (
		round
			? Math.round(px)
			: px
	);
}

/**
 * Move function that calculates the new object state based on the drag deltas.
 * It also ensures that the object remains within the page bounds.
 * @param deltaX Horizontal drag delta
 * @param deltaY Vertical drag delta
 * @param initialObject Initial object state
 * @param pageModel Page model
 * @param options.bleedMargin Thickness of the outer bleed band
 * @param options.pageMargin Thickness of the inner page-margin band
 * @param options.scaling Scaling factor for the object
 * @returns Move result containing the updated object and active snap guides
 */
export function movePageObject(
	deltaX: number,
	deltaY: number,
	initialObject: PageObjectModel,
	pageModel: PageModel,
	options: MovePageObjectOptions,
): MovePageObjectResult {
	const { scaling } = options;
	/**
	 * Ensure initial object's values are valid
	 */
	initialObject = fixObjectBounds(initialObject);
	const {
		x_axis: initialXAxis,
		y_axis: initialYAxis,
	} = initialObject;
	/**
	 * Calculate object movement in global coordinates
	 */
	const moveX = deltaX / scaling;
	const moveY = deltaY / scaling;

	/**
	 * Update the axis and the `snapObjectModel()` function
	 * to apply snapping magnetism in the defined magnetic
	 * lines.
	 * Get both the snapped geometry and UI guides.
	 */
	const snapResult = snapMovedObjectModel(
		{
			...initialObject,
			x_axis: initialXAxis + moveX,
			y_axis: initialYAxis + moveY,
		},
		pageModel,
		options,
	);
	const {
		centerSnapTolerance,
		objectModel: initialSnappedObjectModel,
		pageMagneticLines,
		snapGuides: initialSnapGuides,
	} = snapResult;
	let snappedObjectModel = initialSnappedObjectModel;
	let snapGuides = initialSnapGuides;
	/**
	 * Ensure at least 50 pixels are visible.
	 * Recompute guides afterwards so the overlay
	 * reflects the adjusted geometry.
	 */
	const visibilityAdjustedObject = ensureMinimumVisiblePixels(
		snappedObjectModel,
		pageModel,
	);

	if (visibilityAdjustedObject !== snappedObjectModel) {
		snappedObjectModel = visibilityAdjustedObject;
		snapGuides = getActiveSnapGuides(
			snappedObjectModel,
			pageMagneticLines,
			centerSnapTolerance,
		);
	}

	return {
		objectModel: snappedObjectModel,
		snapGuides,
	};
}

/**
 * Computes updated crop offsets after dragging the photo within its crop viewport.
 *
 * @param deltaX Horizontal drag delta measured in screen pixels.
 * @param deltaY Vertical drag delta measured in screen pixels.
 * @param initialObject Initial {@link PageObjectModel} before applying the drag.
 * @param options {@link MovePageObjectCropOptions} describing the current scaling and rendered crop dimensions.
 * @returns {@link MovePageObjectCropResult} holding the normalized object.
 */
export function movePageObjectCrop(
	deltaX: number,
	deltaY: number,
	initialObject: PageObjectModel,
	options: MovePageObjectCropOptions,
): MovePageObjectCropResult {
	const {
		cropheight,
		cropwidth,
		height,
		width,
	} = initialObject;
	const {
		displayHeight,
		displayWidth,
		scaling,
	} = options;
	const effectiveDisplayWidth = displayWidth ?? width;
	const effectiveDisplayHeight = displayHeight ?? height;

	if (
		!scaling
		|| !cropheight
		|| !cropwidth
		|| !effectiveDisplayHeight
		|| !effectiveDisplayWidth
	) {
		return {
			objectModel: normalizePageObjectCrop(initialObject),
		};
	}

	const cropScaleX = cropwidth / effectiveDisplayWidth;
	const cropScaleY = cropheight / effectiveDisplayHeight;
	const normalizedDeltaCropX = (deltaX / scaling) * cropScaleX;
	const normalizedDeltaCropY = (deltaY / scaling) * cropScaleY;
	const translatedObject = normalizePageObjectCrop({
		...initialObject,
		cropx: initialObject.cropx - normalizedDeltaCropX,
		cropy: initialObject.cropy - normalizedDeltaCropY,
	});

	return {
		objectModel: translatedObject,
	};
}

/**
 * Derives fixed-cover normalization options from the canonical persisted
 * `pageList` order, excluding viewing-only ids and ids missing from `pages`.
 *
 * @param pageIds Persisted page ids in their canonical order.
 * @param pages Optional page lookup used to exclude missing page ids.
 * @returns Cover-page mapping options for viewing-id normalization.
 */
export function getPageListNormalizationOptionsFromPersistedPageList(
	pageIds: PageModel['id'][],
	pages?: Partial<Record<PageModel['id'], PageModel | undefined>>,
): NormalizeViewingPageIdsOptions {
	const persistedPageIds = pageIds.filter((pageId) => (
		resolvePersistedPageIdFromViewingPageId(pageId) === pageId
		&& (
			typeof pages === 'undefined'
			|| !!pages[pageId]
		)
	));

	return {
		backCoverPageId: persistedPageIds[0] ?? null,
		frontCoverPageId: persistedPageIds[1] ?? null,
	};
}

/**
 * Normalizes viewing-model page ids into a unique persisted page id list suitable for `pageList`.
 *
 * @param pageIds Page ids coming from viewing-only UI models.
 * @param options Cover-page mapping options for fixed-cover books.
 * @returns Deduplicated persisted page ids in their original relative order.
 */
export function normalizeViewingPageIdsForPageList(
	pageIds: PageModel['id'][],
	options: NormalizeViewingPageIdsOptions = {},
): PageModel['id'][] {
	const normalizedPageIds: PageModel['id'][] = [];
	const normalizedPageIdsSet = new Set<PageModel['id']>();

	for (const pageId of pageIds) {
		const normalizedPageId = resolvePersistedPageIdFromViewingPageId(
			pageId,
			options,
		);

		if (
			normalizedPageId
			&& !normalizedPageIdsSet.has(normalizedPageId)
		) {
			normalizedPageIds.push(normalizedPageId);
			normalizedPageIdsSet.add(normalizedPageId);
		}
	}

	return normalizedPageIds;
}

/**
 * Reorders viewing-model page ids to match the persisted `pageList` order while
 * keeping viewing-only pages in their original persisted-page slot.
 *
 * @param viewingPageIds Page ids coming from viewing-only UI models.
 * @param persistedPageIds Persisted page ids from `pageList`.
 * @param options Cover-page mapping options for fixed-cover books.
 * @returns Viewing page ids reordered without dropping virtual pages.
 */
export function reorderViewingPageIdsForPageList(
	viewingPageIds: PageModel['id'][],
	persistedPageIds: PageModel['id'][],
	options: NormalizeViewingPageIdsOptions = {},
): PageModel['id'][] {
	const viewingOnlyPageIdsBySlot = new Map<number, PageModel['id'][]>();
	const viewingPageIdsByPersistedId = new Map<PageModel['id'], PageModel['id'][]>();
	let persistedPageSlotIndex = 0;

	for (const viewingPageId of viewingPageIds) {
		const persistedPageId = resolvePersistedPageIdFromViewingPageId(
			viewingPageId,
			options,
		);

		if (!persistedPageId) {
			const slotPageIds = (
				viewingOnlyPageIdsBySlot.get(persistedPageSlotIndex)
				?? []
			);

			slotPageIds.push(viewingPageId);
			viewingOnlyPageIdsBySlot.set(
				persistedPageSlotIndex,
				slotPageIds,
			);
		} else {
			const groupedViewingPageIds = (
				viewingPageIdsByPersistedId.get(persistedPageId)
				?? []
			);

			groupedViewingPageIds.push(viewingPageId);
			viewingPageIdsByPersistedId.set(
				persistedPageId,
				groupedViewingPageIds,
			);
			persistedPageSlotIndex += 1;
		}
	}

	const reorderedViewingPageIds = [...(viewingOnlyPageIdsBySlot.get(0) ?? [])];
	let outputPersistedPageSlotIndex = 0;

	for (const persistedPageId of persistedPageIds) {
		const groupedViewingPageIds = viewingPageIdsByPersistedId.get(persistedPageId);

		if (groupedViewingPageIds?.length) {
			reorderedViewingPageIds.push(...groupedViewingPageIds);
			outputPersistedPageSlotIndex += 1;
			reorderedViewingPageIds.push(
				...(viewingOnlyPageIdsBySlot.get(outputPersistedPageSlotIndex) ?? []),
			);
		}
	}

	return reorderedViewingPageIds;
}

/**
 * Processes a vector logo by loading its SVG content and extracting colors.
 * If the photo model is not provided, it waits for the `photoid` to be set.
 * If the photo model exists but the URL is not set, it waits for the URL to be available.
 * Once the SVG is loaded, it extracts the colors and updates the page object model with
 * the SVG content and colors.
 * @param objectModel The page object model to process
 * @param photoModel The photo model associated with the object
 * @returns A promise that resolves to the updated page object model if successful,
 * or the original object model if no updates are made.
 */
export function processVectorLogo(
	objectModel: PageObjectModel,
	photoModel?: PhotoModel,
): Promise<PageObjectModel> {
	if (!photoModel) {
		/**
		 * If the photo model couldn't not be found and the `photoid` is a `number`,
		 * then we just exit the function since there is nothing to process.
		 */
		if (typeof objectModel.photoid === 'number') {
			return Promise.resolve(objectModel);
		}

		/**
		 * In case the `photoid` is a `string` it might mean that the photo model
		 * was just processed (from an upload) but the object isn't yet updated
		 * with the proper `photoid`, in which case we need to wait for the
		 * `photoid` to be updated.
		 */
		return new Promise((resolve, reject) => {
			watch(
				() => objectModel.photoid,
				() => {
					processVectorLogo(
						objectModel,
						photoModel,
					)
						.then(resolve)
						.catch(reject);
				},
				{
					once: true,
				},
			);
		});
	}

	/**
	 * If the photo model exists but it doesn't have a `url` set yet it means
	 * that the photo model is still being uploaded, in which case we need to
	 * wait until the photo finishes uploading and has its `url` set.
	 */
	if (!photoModel.url) {
		DialogService.openLoaderDialog();
		return new Promise((resolve, reject) => {
			const unwatch = watch(
				() => photoModel.url,
				() => {
					if (photoModel.url) {
						unwatch();
						DialogService.closeLoaderDialog();
						processVectorLogo(
							objectModel,
							photoModel,
						)
							.then(resolve)
							.catch(reject);
					}
				},
			);
		});
	}

	return svgUtils
		.load(photoModel.url)
		.then(async (svgResult) => {
			const svgColors = await svgUtils.getColors(
				svgResult.content,
				ConfigModule['logoColors.mergeSimilarThreshold'],
			);

			return ProductStateModule.changePageObject({
				id: objectModel.id,
				_vectorSVG: svgResult.content,
				_vectorColors: svgColors,
				noStore: true,
				originalPageObject: objectModel,
			});
		})
		.then((updatedPageObject) => (
			updatedPageObject?.updatedPageObject
			?? objectModel
		));
}

/**
 * Converts pixels to inches based on DPI
 * @param px The value in pixels
 * @param dpi The DPI value
 * @returns The value in inches
 */
export function pxToIn(
	px: number,
	dpi: number,
): number {
	const pxPerInch = mmToPx(
		25.4,
		dpi,
	);
	const inches = px / pxPerInch;

	return inches;
}

/**
 * Converts pixels to millimeters based on DPI
 * @param px The value in pixels
 * @param dpi The DPI value
 * @returns The value in millimeters
 */
export function pxToMM(
	px: number,
	dpi: number,
): number {
	const pxPerMm = mmToPx(
		1,
		dpi,
	);
	const mm = px / pxPerMm;

	return mm;
}

/**
 * Resize function that calculates the new object state
 * based on the drag deltas and the resize side.
 * It also ensures at least 50 pixels of the object remain
 * visible within the page.
 * @param deltaX Horizontal drag delta
 * @param deltaY Vertical drag delta
 * @param initialObject Initial object state
 * @param pageModel Page model
 * @param resizeSide Resize side
 * @param options.bleedMargin Thickness of the outer bleed band
 * @param options.pageMargin Thickness of the inner page-margin band
 * @param options.minSizeLimit Minimum size limit for the object
 * @param options.scaling Scaling factor for the object
 * @returns New object state with the updated size
 */
export function resizePageObject(
	deltaX: number,
	deltaY: number,
	initialObject: PageObjectModel,
	pageModel: PageModel,
	resizeSide: EditorInteractivePageObjectResizeSide,
	options: ResizePageObjectOptions,
): PageObjectModel;
/**
 * Resize function that calculates the new object state
 * based on the pinch delta.
 * It also ensures at least 50 pixels of the object remain
 * visible within the page.
 * @param deltaPinch Pinch delta
 * @param initialObject Initial object state
 * @param pageModel Page model
 * @param options.bleedMargin Thickness of the outer bleed band
 * @param options.pageMargin Thickness of the inner page-margin band
 * @param options.minSizeLimit Minimum size limit for the object
 * @param options.scaling Scaling factor for the object
 * @returns New object state with the updated size
 */
export function resizePageObject(
	deltaPinch: number,
	initialObject: PageObjectModel,
	pageModel: PageModel,
	options: ResizePageObjectOptions,
): PageObjectModel;
export function resizePageObject(
	deltaXOrDeltaPinch: number,
	deltaYOrInitialObject: number | PageObjectModel,
	initialObjectOrPageModel: PageObjectModel | PageModel,
	pageModelOrOptions: PageModel | ResizePageObjectOptions,
	resizeSide?: EditorInteractivePageObjectResizeSide,
	options?: ResizePageObjectOptions,
): PageObjectModel {
	let initialObject: PageObjectModel;
	let pageModel: PageModel;

	if (typeof deltaYOrInitialObject !== 'number') {
		initialObject = deltaYOrInitialObject;
		pageModel = initialObjectOrPageModel as PageModel;
		options = pageModelOrOptions as ResizePageObjectOptions;
	} else {
		initialObject = initialObjectOrPageModel as PageObjectModel;
		pageModel = pageModelOrOptions as PageModel;
		resizeSide = resizeSide as EditorInteractivePageObjectResizeSide;
		options = options as ResizePageObjectOptions;
	}

	const {
		maxSizeLimit,
		minSizeLimit,
		scaling,
	} = options;
	initialObject = fixObjectBounds(initialObject);
	const {
		height: initialHeight,
		width: initialWidth,
		rotate,
		type,
	} = initialObject;
	let newObjectModel: PageObjectModel = {
		...initialObject,
	};

	if (resizeSide) {
		/**
		 * Resize happening from a corner/side
		 */
		const initialPoints = getRotatedCorners(initialObject);
		const isPhoto = type === 'photo';
		const cornerHandles = [
			'bottom-right',
			'top-left',
			'top-right',
			'bottom-left',
		];
		const isCorner = (
			resizeSide
			&& cornerHandles.includes(resizeSide)
		);

		const deltaX = deltaXOrDeltaPinch;
		const deltaY = deltaYOrInitialObject as number;

		/**
		 * Rotate the mouse deltas (deltaX, deltaY) into the object’s local
		 * coordinate system using the object's rotate angle
		 */
		const rad = -rotate * DEGREES_TO_RADIANS;
		const cos = Math.cos(rad);
		const sin = Math.sin(rad);
		const deltaXLocal = (deltaX * cos - deltaY * sin) / scaling;
		const deltaYLocal = (deltaX * sin + deltaY * cos) / scaling;

		/**
		 * Search for definition on how each resize side affects
		 * width (widthDirection), height (heightDirection),
		 * and the fixed corner index (fixedCornerIndex)
		 *
		 * E.g.:
		 * - Corners (e.g., bottom-right): Adjust both width and height
		 * - Sides (e.g., right): Adjust only one dimension
		 */
		const resizeParameter = resizeParameters[resizeSide];

		if (!resizeParameter) {
			console.warn(`Invalid resize side: ${resizeSide}`);

			return newObjectModel;
		}

		const {
			fixedCornerIndex,
			heightDirection,
			widthDirection,
		} = resizeParameter;
		let aspectRatio: number | null = null;

		/**
		 * For photo objects resized from corners, maintain the aspect ratio
		 */
		if (
			isPhoto
			&& isCorner
		) {
			aspectRatio = initialHeight / initialWidth;
		}

		let deltaWidth: number;
		let deltaHeight: number;

		if (aspectRatio) {
			deltaWidth = widthDirection * deltaXLocal;
			deltaHeight = deltaWidth * aspectRatio;
		} else {
			deltaWidth = (
				widthDirection !== 0
					? widthDirection * deltaXLocal
					: 0
			);
			deltaHeight = (
				heightDirection !== 0
					? heightDirection * deltaYLocal
					: 0
			);
		}

		/**
		 * Apply full resize (resizeFactor = 1)
		 */
		newObjectModel = calculateObjectResize(
			deltaWidth,
			deltaHeight,
			initialObject,
			{
				aspectRatio,
				fixedCornerIndex,
				initialPoints,
				minSizeLimit,
				maxSizeLimit,
				resizeFactor: 1,
				resizeSide,
			},
		);
	} else {
		/**
		 * Resize happening from the center, pinch-to-zoom use case
		 */
		const deltaPinch = deltaXOrDeltaPinch / scaling;
		const aspectRatio = initialHeight / initialWidth;

		let newWidth = initialWidth + deltaPinch;
		let newHeight = newWidth * aspectRatio;

		if (newWidth < minSizeLimit) {
			newWidth = minSizeLimit;
			newHeight = newWidth * aspectRatio;
		}

		if (newHeight < minSizeLimit) {
			newHeight = minSizeLimit;
			newWidth = newHeight / aspectRatio;
		}

		if (
			typeof maxSizeLimit === 'number'
			&& newWidth > maxSizeLimit
		) {
			newWidth = maxSizeLimit;
			newHeight = newWidth * aspectRatio;
		}

		if (
			typeof maxSizeLimit === 'number'
			&& newHeight > maxSizeLimit
		) {
			newHeight = maxSizeLimit;
			newWidth = newHeight / aspectRatio;
		}

		const centerX = initialObject.x_axis + initialWidth / 2;
		const centerY = initialObject.y_axis + initialHeight / 2;
		const newXAxis = centerX - newWidth / 2;
		const newYAxis = centerY - newHeight / 2;

		newObjectModel.height = newHeight;
		newObjectModel.width = newWidth;
		newObjectModel.x_axis = newXAxis;
		newObjectModel.y_axis = newYAxis;
	}

	/**
	 * Ensure at least 50 pixels are visible
	 */
	newObjectModel = ensureMinimumVisiblePixels(
		newObjectModel,
		pageModel,
	);
	/**
	 * Apply snapping
	 */
	newObjectModel = snapResizedObjectModel(
		newObjectModel,
		pageModel,
		{
			...options,
			resizeSide,
		},
	);

	return newObjectModel;
}

/**
 * Computes updated crop geometry after resizing the crop viewport from one of its handles.
 *
 * @param deltaX Horizontal drag delta measured in screen pixels.
 * @param deltaY Vertical drag delta measured in screen pixels.
 * @param initialObject Initial {@link PageObjectModel} before applying the resize.
 * @param resizeSide {@link EditorInteractivePageObjectResizeSide} describing the active handle.
 * @param options {@link ResizePageObjectCropOptions} describing the current scaling and rendered crop dimensions.
 * @returns {@link ResizePageObjectCropResult} holding the normalized object.
 */
export function resizePageObjectCrop(
	deltaX: number,
	deltaY: number,
	initialObject: PageObjectModel,
	resizeSide: EditorInteractivePageObjectResizeSide,
	options: ResizePageObjectCropOptions,
): ResizePageObjectCropResult {
	const {
		cropheight,
		cropwidth,
		height,
		width,
	} = initialObject;
	const {
		aspectRatioLock = true,
		displayHeight,
		displayWidth,
		scaling,
	} = options;
	const effectiveDisplayWidth = (
		displayWidth
		?? width
	);
	const effectiveDisplayHeight = (
		displayHeight
		?? height
	);

	if (
		!scaling
		|| !cropheight
		|| !cropwidth
		|| !effectiveDisplayHeight
		|| !effectiveDisplayWidth
	) {
		return {
			objectModel: normalizePageObjectCrop(initialObject),
		};
	}

	const cropScaleX = cropwidth / effectiveDisplayWidth;
	const cropScaleY = cropheight / effectiveDisplayHeight;
	const deltaCropX = (deltaX / scaling) * cropScaleX;
	const deltaCropY = (deltaY / scaling) * cropScaleY;
	const cropBounds = getPageObjectCropBounds(initialObject);
	let widthDeltaCandidate: number | undefined;
	let heightDeltaCandidate: number | undefined;

	switch (resizeSide) {
		case 'bottom':
			heightDeltaCandidate = deltaCropY;
			break;
		case 'bottom-left':
			heightDeltaCandidate = deltaCropY;
			widthDeltaCandidate = -deltaCropX;
			break;
		case 'bottom-right':
			heightDeltaCandidate = deltaCropY;
			widthDeltaCandidate = deltaCropX;
			break;
		case 'left':
			widthDeltaCandidate = -deltaCropX;
			break;
		case 'right':
			widthDeltaCandidate = deltaCropX;
			break;
		case 'top':
			heightDeltaCandidate = -deltaCropY;
			break;
		case 'top-left':
			heightDeltaCandidate = -deltaCropY;
			widthDeltaCandidate = -deltaCropX;
			break;
		case 'top-right':
			heightDeltaCandidate = -deltaCropY;
			widthDeltaCandidate = deltaCropX;
			break;
		default:
			break;
	}

	const isSideHandle = (
		resizeSide === 'bottom'
		|| resizeSide === 'left'
		|| resizeSide === 'right'
		|| resizeSide === 'top'
	);
	const isCornerHandle = (
		resizeSide === 'bottom-left'
		|| resizeSide === 'bottom-right'
		|| resizeSide === 'top-left'
		|| resizeSide === 'top-right'
	);

	if (
		!aspectRatioLock
		&& isSideHandle
	) {
		const isHorizontalResize = (
			resizeSide === 'left'
			|| resizeSide === 'right'
		);
		const axisDeltaCandidate = (
			isHorizontalResize
				? widthDeltaCandidate
				: heightDeltaCandidate
		);

		if (typeof axisDeltaCandidate !== 'number') {
			return {
				objectModel: normalizePageObjectCrop(initialObject),
			};
		}

		const currentDimension = (
			isHorizontalResize
				? cropwidth
				: cropheight
		);
		let maxDimension: number;

		if (isHorizontalResize) {
			maxDimension = (
				resizeSide === 'right'
					? cropBounds.maxX - initialObject.cropx
					: initialObject.cropx + cropwidth - cropBounds.minX
			);
		} else if (resizeSide === 'bottom') {
			maxDimension = cropBounds.maxY - initialObject.cropy;
		} else {
			maxDimension = initialObject.cropy + cropheight - cropBounds.minY;
		}

		const boundedMaxDimension = Math.max(
			maxDimension,
			MINIMUM_CROP_SIZE,
		);
		let targetDimension = currentDimension + axisDeltaCandidate;

		targetDimension = Math.min(
			Math.max(
				targetDimension,
				MINIMUM_CROP_SIZE,
			),
			boundedMaxDimension,
		);

		const axisScaleFactor = targetDimension / currentDimension;

		if (
			!Number.isFinite(axisScaleFactor)
			|| axisScaleFactor <= 0
			|| Math.abs(axisScaleFactor - 1) < Number.EPSILON
		) {
			return {
				objectModel: normalizePageObjectCrop(initialObject),
			};
		}

		let newCropHeight = cropheight;
		let newCropWidth = cropwidth;
		let newCropX = initialObject.cropx;
		let newCropY = initialObject.cropy;
		let newHeight = height;
		let newWidth = width;

		if (isHorizontalResize) {
			const rightEdge = initialObject.cropx + cropwidth;

			newCropWidth = cropwidth * axisScaleFactor;
			newWidth = width * axisScaleFactor;

			if (resizeSide === 'left') {
				newCropX = rightEdge - newCropWidth;
			}
		} else {
			const bottomEdge = initialObject.cropy + cropheight;

			newCropHeight = cropheight * axisScaleFactor;
			newHeight = height * axisScaleFactor;

			if (resizeSide === 'top') {
				newCropY = bottomEdge - newCropHeight;
			}
		}

		return {
			objectModel: normalizePageObjectCrop({
				...initialObject,
				cropheight: Math.round(newCropHeight),
				cropwidth: Math.round(newCropWidth),
				cropx: Math.round(newCropX),
				cropy: Math.round(newCropY),
				height: Math.round(newHeight),
				width: Math.round(newWidth),
			}),
		};
	}

	if (
		!aspectRatioLock
		&& isCornerHandle
	) {
		if (
			typeof widthDeltaCandidate !== 'number'
			|| typeof heightDeltaCandidate !== 'number'
		) {
			return {
				objectModel: normalizePageObjectCrop(initialObject),
			};
		}

		const maxWidthDimension = (
			resizeSide === 'bottom-right'
			|| resizeSide === 'top-right'
				? cropBounds.maxX - initialObject.cropx
				: initialObject.cropx + cropwidth - cropBounds.minX
		);
		const maxHeightDimension = (
			resizeSide === 'bottom-right'
			|| resizeSide === 'bottom-left'
				? cropBounds.maxY - initialObject.cropy
				: initialObject.cropy + cropheight - cropBounds.minY
		);
		const boundedMaxWidth = Math.max(
			maxWidthDimension,
			MINIMUM_CROP_SIZE,
		);
		const boundedMaxHeight = Math.max(
			maxHeightDimension,
			MINIMUM_CROP_SIZE,
		);
		let targetWidth = cropwidth + widthDeltaCandidate;
		let targetHeight = cropheight + heightDeltaCandidate;

		targetWidth = Math.min(
			Math.max(
				targetWidth,
				MINIMUM_CROP_SIZE,
			),
			boundedMaxWidth,
		);
		targetHeight = Math.min(
			Math.max(
				targetHeight,
				MINIMUM_CROP_SIZE,
			),
			boundedMaxHeight,
		);

		const widthScaleFactor = targetWidth / cropwidth;
		const heightScaleFactor = targetHeight / cropheight;

		if (
			!Number.isFinite(widthScaleFactor)
			|| !Number.isFinite(heightScaleFactor)
			|| widthScaleFactor <= 0
			|| heightScaleFactor <= 0
			|| (
				Math.abs(widthScaleFactor - 1) < Number.EPSILON
				&& Math.abs(heightScaleFactor - 1) < Number.EPSILON
			)
		) {
			return {
				objectModel: normalizePageObjectCrop(initialObject),
			};
		}

		const rightEdge = initialObject.cropx + cropwidth;
		const bottomEdge = initialObject.cropy + cropheight;
		let newCropX = initialObject.cropx;
		let newCropY = initialObject.cropy;

		if (
			resizeSide === 'bottom-left'
			|| resizeSide === 'top-left'
		) {
			newCropX = rightEdge - targetWidth;
		}
		if (
			resizeSide === 'top-left'
			|| resizeSide === 'top-right'
		) {
			newCropY = bottomEdge - targetHeight;
		}

		return {
			objectModel: normalizePageObjectCrop({
				...initialObject,
				cropheight: Math.round(targetHeight),
				cropwidth: Math.round(targetWidth),
				cropx: Math.round(newCropX),
				cropy: Math.round(newCropY),
				height: Math.round(height * heightScaleFactor),
				width: Math.round(width * widthScaleFactor),
			}),
		};
	}

	const scaleCandidates: number[] = [];

	if (typeof widthDeltaCandidate === 'number') {
		const desiredWidth = cropwidth + widthDeltaCandidate;

		if (desiredWidth > 0) {
			scaleCandidates.push(desiredWidth / cropwidth);
		}
	}

	if (typeof heightDeltaCandidate === 'number') {
		const desiredHeight = cropheight + heightDeltaCandidate;

		if (desiredHeight > 0) {
			scaleCandidates.push(desiredHeight / cropheight);
		}
	}

	const validScaleCandidates = scaleCandidates.filter((candidate) => Number.isFinite(candidate) && candidate > 0);
	const minScaleFactor = Math.max(
		MINIMUM_CROP_SIZE / cropwidth,
		MINIMUM_CROP_SIZE / cropheight,
	);
	const isShrinking = (
		(
			typeof widthDeltaCandidate === 'number'
			&& widthDeltaCandidate < 0
		)
		|| (
			typeof heightDeltaCandidate === 'number'
			&& heightDeltaCandidate < 0
		)
	);
	let scaleFactor = 1;

	if (validScaleCandidates.length === 0) {
		if (!isShrinking) {
			return {
				objectModel: normalizePageObjectCrop(initialObject),
			};
		}

		scaleFactor = minScaleFactor;
	} else {
		scaleFactor = validScaleCandidates.reduce<number>(
			(selected, candidate) => (
				Math.abs(candidate - 1) > Math.abs(selected - 1)
					? candidate
					: selected
			),
			1,
		);
	}

	const maxHorizontalScaleFactor = (() => {
		const left = initialObject.cropx;
		const right = left + cropwidth;
		const centerX = left + (cropwidth / 2);

		switch (resizeSide) {
			case 'left':
			case 'bottom-left':
			case 'top-left':
				return (right - cropBounds.minX) / cropwidth;
			case 'right':
			case 'bottom-right':
			case 'top-right':
				return (cropBounds.maxX - left) / cropwidth;
			case 'bottom':
			case 'top': {
				const halfWidth = Math.min(
					centerX - cropBounds.minX,
					cropBounds.maxX - centerX,
				);

				return (halfWidth * 2) / cropwidth;
			}
			default:
				return Number.POSITIVE_INFINITY;
		}
	})();
	const maxVerticalScaleFactor = (() => {
		const top = initialObject.cropy;
		const bottom = top + cropheight;
		const centerY = top + (cropheight / 2);

		switch (resizeSide) {
			case 'top':
			case 'top-left':
			case 'top-right':
				return (bottom - cropBounds.minY) / cropheight;
			case 'bottom':
			case 'bottom-left':
			case 'bottom-right':
				return (cropBounds.maxY - top) / cropheight;
			case 'left':
			case 'right': {
				const halfHeight = Math.min(
					centerY - cropBounds.minY,
					cropBounds.maxY - centerY,
				);

				return (halfHeight * 2) / cropheight;
			}
			default:
				return Number.POSITIVE_INFINITY;
		}
	})();
	const maxScaleFactor = Math.min(
		maxHorizontalScaleFactor,
		maxVerticalScaleFactor,
	);
	const boundedMaxScaleFactor = Math.max(
		maxScaleFactor,
		minScaleFactor,
	);

	scaleFactor = Math.min(
		Math.max(
			scaleFactor,
			minScaleFactor,
		),
		boundedMaxScaleFactor,
	);

	if (Math.abs(scaleFactor - 1) < Number.EPSILON) {
		return {
			objectModel: normalizePageObjectCrop(initialObject),
		};
	}

	let newCropWidth = cropwidth * scaleFactor;
	let newCropHeight = cropheight * scaleFactor;
	const centerX = initialObject.cropx + (cropwidth / 2);
	const centerY = initialObject.cropy + (cropheight / 2);
	const right = initialObject.cropx + cropwidth;
	const bottom = initialObject.cropy + cropheight;
	let newCropX = initialObject.cropx;
	let newCropY = initialObject.cropy;

	switch (resizeSide) {
		case 'bottom':
			newCropX = centerX - (newCropWidth / 2);
			newCropY = initialObject.cropy;
			break;
		case 'bottom-left':
			newCropX = right - newCropWidth;
			newCropY = initialObject.cropy;
			break;
		case 'bottom-right':
			newCropX = initialObject.cropx;
			newCropY = initialObject.cropy;
			break;
		case 'left':
			newCropX = right - newCropWidth;
			newCropY = centerY - (newCropHeight / 2);
			break;
		case 'right':
			newCropX = initialObject.cropx;
			newCropY = centerY - (newCropHeight / 2);
			break;
		case 'top':
			newCropX = centerX - (newCropWidth / 2);
			newCropY = bottom - newCropHeight;
			break;
		case 'top-left':
			newCropX = right - newCropWidth;
			newCropY = bottom - newCropHeight;
			break;
		case 'top-right':
			newCropX = initialObject.cropx;
			newCropY = bottom - newCropHeight;
			break;
		default:
			break;
	}

	newCropWidth = Math.min(
		newCropWidth,
		cropBounds.width,
	);
	newCropHeight = Math.min(
		newCropHeight,
		cropBounds.height,
	);

	const resizedObject = normalizePageObjectCrop({
		...initialObject,
		cropheight: Math.round(newCropHeight),
		cropwidth: Math.round(newCropWidth),
		cropx: Math.round(newCropX),
		cropy: Math.round(newCropY),
	});

	return {
		objectModel: resizedObject,
	};
}

/**
 * Resolves a viewing-only page id to the persisted page id that may be stored in `pageList`.
 *
 * @param pageId Page id coming from a viewing model.
 * @param options Cover-page mapping options for fixed-cover books.
 * @returns Persisted page id, or `null` when the id should never be written to `pageList`.
 */
export function resolvePersistedPageIdFromViewingPageId(
	pageId: PageModel['id'],
	options: NormalizeViewingPageIdsOptions = {},
): PageModel['id'] | null {
	if (typeof pageId !== 'string') {
		return pageId;
	}

	if (
		pageId === COVER_FRONT_INSIDE
		|| pageId === COVER_BACK_INSIDE
		|| pageId === SPINE
		|| pageId.startsWith(BACKEND_ONLY_FIXED_PAGE_ID_PREFIX)
	) {
		return null;
	}

	if (pageId === COVER_BACK) {
		return options.backCoverPageId ?? null;
	}

	if (pageId === COVER_FRONT) {
		return options.frontCoverPageId ?? null;
	}

	return pageId;
}

/**
 * Rotate function that calculates the new object state
 * based on the angular difference between the pointer's
 * initial and current positions around the object's pivot.
 *
 * The rotation value is:
 * - **Delta-based**: `newRotation = initialRotation + Δθ`
 * - **Normalized** to the range **0 – 359 deg**
 * - **Rounded** to the nearest whole degree
 * - **Snapped** inside the configured rotation ranges (e.g. 0–5°)
 *
 * @param initialPositionRadians Angle (in radians) from the object pivot
 * to the pointer
 * @param newPositionRadians Angle (in radians) from the object pivot
 * to the pointer
 * @param initialObject Initial object state
 * @returns New object state with the updated rotation
 */
export function rotatePageObject(
	initialPositionRadians: number,
	newPositionRadians: number,
	initialObject: PageObjectModel,
): PageObjectModel {
	const { rotate } = initialObject;
	const newObjectModel: PageObjectModel = {
		...initialObject,
	};
	/**
	 * Angular offset between the pointer and the object's current
	 * orientation **at the moment the drag started**.
	 *
	 * offset = pointerStart - initialObject.rotate
	 *
	 * Everything afterwards is measured relative to this offset so the
	 * cursor can wander away and still return perfectly to the handle.
	 */
	const initialAngularOffset = initialPositionRadians - (rotate * DEGREES_TO_RADIANS);
	/**
	 * Δθ in **radians** between the current pointer position and the
	 * stored offset. This is the raw rotation we want to apply.
	 */
	const deltaRadians = newPositionRadians - initialAngularOffset;
	/**
	 * Convert to **degrees** and round to the nearest whole degree.
	 * Using whole degrees keeps UI feedback predictable and avoids the
	 * tiny deltas that accumulate when users stop moving the mouse.
	 */
	const newRotate = Math.round(deltaRadians * RADIANS_TO_DEGREES);

	/**
	 * Normalize the result to the range **0 – 359** so downstream code
	 * never has to handle negatives or values >= 360.
	 * - The first `% 360` keeps the value within `(-360, 360)` but may leave
	 * it negative because JavaScript's remainder keeps the sign of the dividend.
	 * - Adding `360` shifts any negative remainder into the positive range.
	 * - The second `% 360` then wraps `360` back to `0`, so you never get `360`
	 * as a final result--only `0` through `359`.
	 */
	const normalizedRotation = ((newRotate % 360) + 360) % 360;
	/**
	 * Convert to a signed representation (-180 to 180) so we can evaluate
	 * both the positive and negative snapping windows without duplicating logic.
	 */
	const signedRotation = (
		normalizedRotation > 180
			? normalizedRotation - 360
			: normalizedRotation
	);
	const rotationMagnitude = Math.abs(signedRotation);
	const rotationSign = (
		signedRotation >= 0
			? 1
			: -1
	);
	let snappedRotation = signedRotation;

	/**
	 * Snap the rotation to the nearest target defined for the absolute
	 * range that captures the current magnitude. Negative angles reuse the
	 * same targets by mirroring their magnitude around 0°.
	 */
	for (const range of ROTATION_SNAP_ABSOLUTE_RANGES) {
		if (
			rotationMagnitude >= range.start
			&& rotationMagnitude <= range.end
		) {
			const snappedMagnitude = range.targets.reduce(
				(closest, target) => {
					const difference = Math.abs(rotationMagnitude - target);

					if (difference < Math.abs(rotationMagnitude - closest)) {
						return target;
					}

					return closest;
				},
				range.targets[0],
			);
			snappedRotation = (
				rotationSign < 0
					? -snappedMagnitude
					: snappedMagnitude
			);
			break;
		}
	}

	newObjectModel.rotate = ((snappedRotation % 360) + 360) % 360;

	return newObjectModel;
}

export function shouldExcludeEmptyPages(offeringModel?: OfferingModel | null): boolean {
	/**
	 * Note: Upon migration to JSON schema this check can be performed directly on the property
	 * in the offering data, but for now we need to compile the boolean value like this
	 */
	if (
		offeringModel?.type === 'logo'
		&& offeringModel.maxpages > offeringModel.minprintpages
	) {
		return true;
	}

	return false;
}

/**
 * NOT USED AT THE MOMENT, since when color are grouped together there is an unwanted behavior
 * which is that the shadow is applied to the each single color element instead of the whole group
 * as one element.
 */
export function svgAddHoveredColorEffect(
	svgElement: SVGElement,
	hoveredColor?: string,
): void {
	if (hoveredColor) {
		const filterElement = document.createElementNS(
			'http://www.w3.org/2000/svg',
			'filter',
		);
		filterElement.id = 'shadow';
		const feDropShadowElement = document.createElementNS(
			'http://www.w3.org/2000/svg',
			'feDropShadow',
		);
		feDropShadowElement.setAttribute(
			'dx',
			'0',
		);
		feDropShadowElement.setAttribute(
			'dy',
			'0',
		);
		feDropShadowElement.setAttribute(
			'stdDeviation',
			'2',
		);
		feDropShadowElement.setAttribute(
			'flood-opacity',
			'0.4',
		);
		filterElement.appendChild(feDropShadowElement);
		svgElement.prepend(filterElement);
		svgElement
			.querySelectorAll<HTMLElement>('*')
			.forEach((element) => {
				const tagString = element.outerHTML.replace(
					element.innerHTML,
					'',
				);
				svgUtils.getColorMatches(
					tagString,
					(color) => {
						if (hoveredColor.toLowerCase() === color.toLowerCase()) {
							element.style.filter = 'url(#shadow)';
						}
					},
				);
			});
	}
}

export function svgAddPrintEffect(
	svgElement: SVGElement,
	printEffect: OfferingModel['printEffect'],
): void {
	if (
		printEffect === 'embossing'
		|| printEffect === 'engraving'
		|| printEffect === 'laser engraving'
	) {
		const filterElement = document.createElementNS(
			'http://www.w3.org/2000/svg',
			'filter',
		);
		filterElement.id = 'embossing';
		const feCovolveMatrixElement = document.createElementNS(
			'http://www.w3.org/2000/svg',
			'feConvolveMatrix',
		);
		feCovolveMatrixElement.setAttribute(
			'order',
			'3',
		);
		feCovolveMatrixElement.setAttribute(
			'kernelMatrix',
			'0 1 3 0 0 0 -3 0 0',
		);
		feCovolveMatrixElement.setAttribute(
			'divisor',
			'1',
		);
		feCovolveMatrixElement.setAttribute(
			'bias',
			'0',
		);
		filterElement.appendChild(feCovolveMatrixElement);
		svgElement.prepend(filterElement);
		svgElement.style.filter = 'url(#embossing)';
	}
}

export function updateTextObject(options: UpdateTextObjectOptions): Promise<PageObjectModelChangeResult | undefined> {
	const {
		objectModel,
		offeringModel,
		pageModel,
	} = options;
	let { newData } = options;
	/**
	 * Clone data to avoid mutating the original object
	 */
	newData = {
		...newData,
	};
	const fontModel = (
		objectModel.fontface
		&& FontModule.getById(objectModel.fontface)
	);

	if (!fontModel) {
		throw new Error('Could not find required font model');
	}

	if (typeof newData.text !== 'undefined') {
		newData.text = filter(
			newData.text,
			fontModel.subset.split(','),
		);
	}

	let fontSize: number;

	if (
		newData.fontsize
		|| newData.pointsize
	) {
		fontSize = (
			newData.fontsize
			?? pointToPixel(newData.pointsize!)
		);
	} else {
		fontSize = (
			objectModel.fontsize
			?? pointToPixel(objectModel.pointsize!)
		);
	}

	const mergedObjectModel: PageObjectModel = {
		...objectModel,
		...newData,
		fontsize: fontSize,
	};

	if (
		newData.height
		|| newData.width
	) {
		const fitResult = fit({
			fontFamily: getFontFamily({
				family: mergedObjectModel.fontface!,
				bold: !!mergedObjectModel.fontbold,
				italic: !!mergedObjectModel.fontitalic,
			}),
			fontModel: FontModule.getById(mergedObjectModel.fontface!)!,
			fontSize,
			isBold: !!mergedObjectModel.fontbold,
			maxWidth: mergedObjectModel.width,
			text: (
				mergedObjectModel.text
				?? ''
			),

			kind: 'font',
			boxHeight: mergedObjectModel.height,
			boxWidth: mergedObjectModel.width,
			minFontSize: (
				offeringModel?.minfontsize
					? pointToPixel(offeringModel.minfontsize)
					: MIN_FONTSIZE
			),
			maxFontSize: pageModel.height,
		});

		if (mergedObjectModel.fontsize !== fitResult.usedFontSize) {
			newData.fontsize = fitResult.usedFontSize;
			fontSize = fitResult.usedFontSize;
		}
	} else if (
		(
			typeof newData.text !== 'undefined'
			|| typeof newData.fontbold !== 'undefined'
			|| typeof newData.fontface !== 'undefined'
			|| typeof newData.fontitalic !== 'undefined'
			|| typeof newData.fontunderline !== 'undefined'
			|| typeof newData.fontsize !== 'undefined'
			|| typeof newData.pointsize !== 'undefined'
		)
		&& objectModel.transformable
	) {
		const fitResult = fit(
			{
				fontFamily: getFontFamily({
					family: mergedObjectModel.fontface!,
					bold: !!mergedObjectModel.fontbold,
					italic: !!mergedObjectModel.fontitalic,
				}),
				fontModel: FontModule.getById(mergedObjectModel.fontface!),
				fontSize,
				isBold: !!mergedObjectModel.fontbold,
				maxWidth: mergedObjectModel.width,
				text: (
					mergedObjectModel.text
					?? ''
				),

				kind: 'box',
				allowGrow: true,
				allowShrink: true,
			},
		);

		if (fitResult.height !== mergedObjectModel.height) {
			newData.height = Math.round(fitResult.height);
		}
		if (fitResult.usedFontSize !== fontSize) {
			newData.fontsize = fitResult.usedFontSize;
		}
	}

	return ProductStateModule.changePageObject({
		id: objectModel.id,
		...newData,
		noStore: true,
		originalPageObject: objectModel,
	});
}

/**
 * Calculate the new crop values for an object with a given zoom
 * @param object {@link PageObjectModel} to zoom.
 * @param zoom Zoom percentage (1-100) to apply
 * @returns Decimal-capable crop values constrained to the active crop bounds.
 */
export function zoomObject(
	object: PageObjectModel,
	zoom: number,
): ZoomObjectResult {
	let newCropHeight: number;
	let newCropWidth: number;
	let inverseZoom = 100 - zoom;
	const cropBounds = getPageObjectCropBounds(object);

	if (
		!cropBounds.width
		|| !cropBounds.height
	) {
		return {
			cropheight: object.cropheight,
			cropwidth: object.cropwidth,
			cropx: object.cropx,
			cropy: object.cropy,
		};
	}

	if (inverseZoom < 1) {
		inverseZoom = 1;
	} else if (inverseZoom > 100) {
		inverseZoom = 100;
	}

	if (cropBounds.height < cropBounds.width) {
		newCropHeight = (cropBounds.height * inverseZoom) / 100;
		newCropWidth = getCropFromRatio({
			object,
			type: 'width',
			value: newCropHeight,
		});
	} else {
		newCropWidth = (cropBounds.width * inverseZoom) / 100;
		newCropHeight = getCropFromRatio({
			object,
			type: 'height',
			value: newCropWidth,
		});
	}

	let newCropX = object.cropx + ((object.cropwidth - newCropWidth) / 2);
	let newCropY = object.cropy + ((object.cropheight - newCropHeight) / 2);
	/**
	 * Iteratively preserving the crop ratio can push the opposite axis outside
	 * the active bounds; cap the adjustment loop as a defensive guard.
	 */
	const maxIteration = 1000;
	let iterationNumber = 0;

	while (
		(
			newCropX < cropBounds.minX
			|| newCropY < cropBounds.minY
			|| newCropX + newCropWidth > cropBounds.maxX
			|| newCropY + newCropHeight > cropBounds.maxY
		)
		&& iterationNumber < maxIteration
	) {
		if (newCropX < cropBounds.minX) {
			newCropWidth -= newCropX - cropBounds.minX;
			newCropHeight = getCropFromRatio({
				object,
				type: 'height',
				value: newCropWidth,
			});
			newCropX = cropBounds.minX;
			newCropY = object.cropy + ((object.cropheight - newCropHeight) / 2);

			if (newCropY < cropBounds.minY) {
				newCropY = cropBounds.minY;
			}
		}
		if (newCropWidth > cropBounds.width) {
			newCropWidth = cropBounds.width;
			newCropHeight = getCropFromRatio({
				object,
				type: 'height',
				value: newCropWidth,
			});
			newCropX = cropBounds.minX;
			newCropY = object.cropy + ((object.cropheight - newCropHeight) / 2);

			if (newCropY < cropBounds.minY) {
				newCropY = cropBounds.minY;
			}
		}
		if (newCropX + newCropWidth > cropBounds.maxX) {
			newCropX -= (newCropX + newCropWidth) - cropBounds.maxX;
		}
		if (newCropY < cropBounds.minY) {
			newCropHeight -= newCropY - cropBounds.minY;
			newCropWidth = getCropFromRatio({
				object,
				type: 'width',
				value: newCropHeight,
			});
			newCropY = cropBounds.minY;
			newCropX = object.cropx + ((object.cropwidth - newCropWidth) / 2);

			if (newCropX < cropBounds.minX) {
				newCropX = cropBounds.minX;
			}
		}
		if (newCropHeight > cropBounds.height) {
			newCropHeight = cropBounds.height;
			newCropWidth = getCropFromRatio({
				object,
				type: 'width',
				value: newCropHeight,
			});
			newCropY = cropBounds.minY;
			newCropX = object.cropx + ((object.cropwidth - newCropWidth) / 2);

			if (newCropX < cropBounds.minX) {
				newCropX = cropBounds.minX;
			}
		}
		if (newCropY + newCropHeight > cropBounds.maxY) {
			newCropY -= (newCropY + newCropHeight) - cropBounds.maxY;
		}

		iterationNumber += 1;
	}

	return {
		cropheight: newCropHeight,
		cropwidth: newCropWidth,
		cropx: newCropX,
		cropy: newCropY,
	};
}
